//#region src/ai.ts
function getAllModelsFromConfig(config) {
	return Object.values(config).flat();
}
function isValidModelInConfig(model, config) {
	return getAllModelsFromConfig(config).includes(model);
}

//#endregion
//#region src/api-limits.ts
const API_REQUEST_BODY_LIMIT_BYTES = 50 * 1024 * 1024;

//#endregion
//#region src/config.ts
const isNode$1 = typeof process !== "undefined" && process.versions != null && process.versions.node != null;
const PORT = {
	API: 3001,
	WEB: 3e3,
	WEB_ADMIN: 3002,
	QUEUE: 5001
};
const MENTISS_REDIS_DB = {
	API: 1,
	QUEUE: 2
};
const MENTISS_REDIS_HOST = isNode$1 && process.env.MENTISS_REDIS_HOST ? process.env.MENTISS_REDIS_HOST : "127.0.0.1";
const MENTISS_REDIS_PORT = isNode$1 && process.env.MENTISS_REDIS_PORT ? Number.parseInt(process.env.MENTISS_REDIS_PORT, 10) : 6379;

//#endregion
//#region src/roster/setting-mode.ts
const namedSettingModes = [
	"USER_CUSTOM_CONFIG",
	"HUMAN_VS_AI",
	"WATCH_MODE",
	"MODEL_COMPARISON",
	"BYOM"
];
function readSettingMode(setting) {
	const terminalSuffix = setting.match(/-([A-Z])$/)?.[1] ?? null;
	const symbolicMatch = setting.match(/^[GD](10|12)_ULTIMATE_TRIAL(?:-[RS])?$/);
	const symbolicPlayerCount = symbolicMatch ? Number(symbolicMatch[1]) : null;
	const namedMode = namedSettingModes.find((mode$1) => mode$1 === setting) ?? null;
	const prefixMatch = setting.match(/^([GD])(\d+)_/);
	const declaredPrefix = prefixMatch ? {
		kind: prefixMatch[1],
		digits: prefixMatch[2],
		count: Number.parseInt(prefixMatch[2], 10)
	} : null;
	let count = declaredPrefix?.count ?? 0;
	if (!declaredPrefix) for (const match of setting.matchAll(/(\d+)[A-Z]{2}/g)) count += Number.parseInt(match[1], 10);
	const advancedMatch = setting.match(/-([WBC])(?:-S)?$/);
	const advancedBeforeS = advancedMatch ? advancedMatch[1] : null;
	const mode = symbolicPlayerCount !== null || terminalSuffix === "R" ? "random" : namedMode === "WATCH_MODE" || advancedBeforeS === "W" ? "watch" : namedMode === "MODEL_COMPARISON" || advancedBeforeS === "C" ? "comparison" : namedMode === "BYOM" || advancedBeforeS === "B" ? "byom" : "standard";
	const legacyFlags = terminalSuffix === "S" || terminalSuffix === "H" || terminalSuffix === "U" ? [terminalSuffix] : [];
	return {
		mode,
		dataGame: setting.startsWith("D"),
		legacyFlags,
		playerCount: Number.isInteger(count) && count > 0 ? count : null,
		terminalSuffix,
		symbolicPlayerCount,
		namedMode,
		declaredPrefix,
		markers: {
			containsR: setting.includes("-R"),
			containsH: setting.includes("-H"),
			containsTrial: setting.includes("ULTIMATE_TRIAL"),
			randomSuffixSegment: /-R(?:-|$)/.test(setting)
		},
		advancedBeforeS
	};
}
function writeSetting(query) {
	switch (query.kind) {
		case "carry-mode": {
			const suffix = query.setting?.match(/-([RWCB])$/)?.[1];
			return `${query.canonicalBase}${suffix ? `-${suffix}` : ""}`;
		}
		case "select-mode": return `${query.setting.replace(/-U$/, "")}-${query.mode}`;
		case "append-roster-mode": return `${query.setting}-${query.suffix}`;
		case "normalize": {
			const normalizedSetting = query.setting.endsWith("-S") ? query.setting.slice(0, -2) : query.setting;
			if (normalizedSetting.endsWith("-U")) return normalizedSetting.slice(0, -2);
			const suffix = normalizedSetting.match(/-([RWCB])$/)?.[0] ?? "";
			const baseSetting = suffix ? normalizedSetting.slice(0, -suffix.length) : normalizedSetting;
			const fixedCode = query.fixedCode(normalizedSetting);
			if (fixedCode) return fixedCode;
			const { symbolicPlayerCount } = readSettingMode(baseSetting);
			return symbolicPlayerCount ? query.drawBrawl(baseSetting.startsWith("D") ? "data" : "game", symbolicPlayerCount) : normalizedSetting;
		}
		case "strip-display-style": return query.setting.replace(/-[RWCBSH]$/, "");
		case "strip-display-parse": return query.setting.replace(/-[RWCBSHU]$/, "");
		case "strip-archive": return query.setting.replace(/-[A-Z]$/, "");
		case "strip-requested-preset": return query.setting.endsWith("-S") ? query.setting.slice(0, -2) : query.setting;
	}
}

//#endregion
//#region src/constant.ts
const DataSettingOptions = {
	D6_1SR1WT_2WW_2VG: "D6_1SR1WT_2WW_2VG",
	D9_1HT1SR1WT_3WW_3VG: "D9_1HT1SR1WT_3WW_3VG",
	D10_ULTIMATE_TRIAL: "D10_ULTIMATE_TRIAL",
	D12_ULTIMATE_TRIAL: "D12_ULTIMATE_TRIAL"
};
const GameSettingOptions = {
	G6_1SR1WT_2WW_2VG: "G6_1SR1WT_2WW_2VG",
	G9_1HT1SR1WT_3WW_3VG: "G9_1HT1SR1WT_3WW_3VG",
	G10_ULTIMATE_TRIAL: "G10_ULTIMATE_TRIAL",
	G12_ULTIMATE_TRIAL: "G12_ULTIMATE_TRIAL",
	G10_NIGHTMARE: "G10_NIGHTMARE",
	G10_DREAMER: "G10_DREAMER",
	G10_PSYCHIC_MECHANICAL: "G10_PSYCHIC_MECHANICAL",
	G10_WOLF_BROTHERS_BLACK_MARKET: "G10_WOLF_BROTHERS_BLACK_MARKET",
	G10_WOLF_BEAUTY_KNIGHT: "G10_WOLF_BEAUTY_KNIGHT",
	G10_GARGOYLE_GRAVEKEEPER: "G10_GARGOYLE_GRAVEKEEPER",
	G10_BLOOD_MOON_DEMON_HUNTER: "G10_BLOOD_MOON_DEMON_HUNTER",
	G10_ALPHA_WOLF_MAGICIAN: "G10_ALPHA_WOLF_MAGICIAN",
	G10_WRAITH_KNIGHT: "G10_WRAITH_KNIGHT",
	G10_SNOW_WOLF_ALPHA_KNIGHT: "G10_SNOW_WOLF_ALPHA_KNIGHT",
	G12_NIGHTMARE: "G12_NIGHTMARE",
	G12_DREAMER: "G12_DREAMER",
	G12_PSYCHIC_MECHANICAL: "G12_PSYCHIC_MECHANICAL",
	G12_WOLF_BROTHERS_BLACK_MARKET: "G12_WOLF_BROTHERS_BLACK_MARKET",
	G12_WOLF_BEAUTY_KNIGHT: "G12_WOLF_BEAUTY_KNIGHT",
	G12_GARGOYLE_GRAVEKEEPER: "G12_GARGOYLE_GRAVEKEEPER",
	G12_BLOOD_MOON_DEMON_HUNTER: "G12_BLOOD_MOON_DEMON_HUNTER",
	G12_ALPHA_WOLF_MAGICIAN: "G12_ALPHA_WOLF_MAGICIAN",
	G12_WRAITH_KNIGHT: "G12_WRAITH_KNIGHT",
	G12_SNOW_WOLF_ALPHA_KNIGHT: "G12_SNOW_WOLF_ALPHA_KNIGHT",
	USER_CUSTOM_CONFIG: "USER_CUSTOM_CONFIG",
	HUMAN_VS_AI: "HUMAN_VS_AI",
	WATCH_MODE: "WATCH_MODE",
	MODEL_COMPARISON: "MODEL_COMPARISON",
	BYOM: "BYOM"
};
const ANONYMOUS_FREE_GAME_SETTING = GameSettingOptions.G6_1SR1WT_2WW_2VG;
const FREE_GAME_MAX_PLAYERS = 10;
function getPlayerCountFromGameSetting(gameSetting) {
	return readSettingMode(gameSetting).playerCount;
}
const PlayerRole = {
	seer: "seer",
	witch: "witch",
	villager: "villager",
	werewolf: "werewolf",
	hunter: "hunter",
	gravekeeper: "gravekeeper",
	gargoyle: "gargoyle",
	snow_wolf: "snow_wolf",
	alpha_wolf: "alpha_wolf",
	knight: "knight",
	demon_hunter: "demon_hunter",
	guard: "guard",
	black_market_dealer: "black_market_dealer",
	wraith_knight: "wraith_knight",
	blood_moon_herald: "blood_moon_herald",
	wolf_beauty: "wolf_beauty",
	magician: "magician",
	moron: "moron",
	wolf_elder: "wolf_elder",
	wolf_younger: "wolf_younger",
	nightmare: "nightmare",
	dreamer: "dreamer",
	psychic: "psychic",
	mechanical_wolf: "mechanical_wolf"
};
const RoleCode = {
	seer: "SR",
	witch: "WT",
	villager: "VG",
	werewolf: "WW",
	hunter: "HT",
	gravekeeper: "GK",
	gargoyle: "GY",
	snow_wolf: "SW",
	alpha_wolf: "AW",
	knight: "KN",
	demon_hunter: "DH",
	guard: "GD",
	black_market_dealer: "BD",
	wraith_knight: "WK",
	blood_moon_herald: "BH",
	wolf_beauty: "WB",
	magician: "MG",
	moron: "MR",
	wolf_elder: "WE",
	wolf_younger: "WY",
	nightmare: "NM",
	dreamer: "DR",
	psychic: "PY",
	mechanical_wolf: "MW"
};
const RoleFaction = {
	seer: "good",
	witch: "good",
	villager: "good",
	hunter: "good",
	werewolf: "evil",
	snow_wolf: "evil",
	alpha_wolf: "evil",
	knight: "good",
	guard: "good",
	demon_hunter: "good",
	black_market_dealer: "good",
	gravekeeper: "good",
	wraith_knight: "evil",
	blood_moon_herald: "evil",
	gargoyle: "evil",
	wolf_beauty: "evil",
	magician: "good",
	moron: "good",
	wolf_elder: "evil",
	wolf_younger: "evil",
	nightmare: "evil",
	dreamer: "good",
	psychic: "good",
	mechanical_wolf: "evil"
};
const FIXED_GAME_ROSTERS = {
	[GameSettingOptions.G10_NIGHTMARE]: [
		PlayerRole.seer,
		PlayerRole.witch,
		PlayerRole.hunter,
		PlayerRole.guard,
		PlayerRole.villager,
		PlayerRole.villager,
		PlayerRole.villager,
		PlayerRole.nightmare,
		PlayerRole.werewolf,
		PlayerRole.werewolf
	],
	[GameSettingOptions.G12_NIGHTMARE]: [
		PlayerRole.seer,
		PlayerRole.witch,
		PlayerRole.hunter,
		PlayerRole.guard,
		PlayerRole.villager,
		PlayerRole.villager,
		PlayerRole.villager,
		PlayerRole.villager,
		PlayerRole.nightmare,
		PlayerRole.werewolf,
		PlayerRole.werewolf,
		PlayerRole.werewolf
	],
	[GameSettingOptions.G10_DREAMER]: [
		PlayerRole.dreamer,
		PlayerRole.seer,
		PlayerRole.hunter,
		PlayerRole.witch,
		PlayerRole.villager,
		PlayerRole.villager,
		PlayerRole.villager,
		PlayerRole.alpha_wolf,
		PlayerRole.werewolf,
		PlayerRole.werewolf
	],
	[GameSettingOptions.G10_PSYCHIC_MECHANICAL]: [
		PlayerRole.psychic,
		PlayerRole.guard,
		PlayerRole.hunter,
		PlayerRole.witch,
		PlayerRole.villager,
		PlayerRole.villager,
		PlayerRole.villager,
		PlayerRole.mechanical_wolf,
		PlayerRole.werewolf,
		PlayerRole.werewolf
	],
	[GameSettingOptions.G10_WOLF_BROTHERS_BLACK_MARKET]: [
		PlayerRole.seer,
		PlayerRole.witch,
		PlayerRole.guard,
		PlayerRole.black_market_dealer,
		PlayerRole.villager,
		PlayerRole.villager,
		PlayerRole.villager,
		PlayerRole.wolf_elder,
		PlayerRole.wolf_younger,
		PlayerRole.werewolf
	],
	[GameSettingOptions.G10_WOLF_BEAUTY_KNIGHT]: [
		PlayerRole.seer,
		PlayerRole.witch,
		PlayerRole.guard,
		PlayerRole.knight,
		PlayerRole.villager,
		PlayerRole.villager,
		PlayerRole.villager,
		PlayerRole.wolf_beauty,
		PlayerRole.werewolf,
		PlayerRole.werewolf
	],
	[GameSettingOptions.G10_GARGOYLE_GRAVEKEEPER]: [
		PlayerRole.seer,
		PlayerRole.witch,
		PlayerRole.hunter,
		PlayerRole.gravekeeper,
		PlayerRole.villager,
		PlayerRole.villager,
		PlayerRole.villager,
		PlayerRole.gargoyle,
		PlayerRole.werewolf,
		PlayerRole.werewolf
	],
	[GameSettingOptions.G10_BLOOD_MOON_DEMON_HUNTER]: [
		PlayerRole.seer,
		PlayerRole.witch,
		PlayerRole.demon_hunter,
		PlayerRole.moron,
		PlayerRole.villager,
		PlayerRole.villager,
		PlayerRole.villager,
		PlayerRole.blood_moon_herald,
		PlayerRole.werewolf,
		PlayerRole.werewolf
	],
	[GameSettingOptions.G10_ALPHA_WOLF_MAGICIAN]: [
		PlayerRole.seer,
		PlayerRole.witch,
		PlayerRole.hunter,
		PlayerRole.magician,
		PlayerRole.villager,
		PlayerRole.villager,
		PlayerRole.villager,
		PlayerRole.alpha_wolf,
		PlayerRole.werewolf,
		PlayerRole.werewolf
	],
	[GameSettingOptions.G10_WRAITH_KNIGHT]: [
		PlayerRole.seer,
		PlayerRole.witch,
		PlayerRole.hunter,
		PlayerRole.guard,
		PlayerRole.villager,
		PlayerRole.villager,
		PlayerRole.villager,
		PlayerRole.wraith_knight,
		PlayerRole.werewolf,
		PlayerRole.werewolf
	],
	[GameSettingOptions.G10_SNOW_WOLF_ALPHA_KNIGHT]: [
		PlayerRole.seer,
		PlayerRole.witch,
		PlayerRole.hunter,
		PlayerRole.knight,
		PlayerRole.villager,
		PlayerRole.villager,
		PlayerRole.villager,
		PlayerRole.alpha_wolf,
		PlayerRole.snow_wolf,
		PlayerRole.werewolf
	],
	[GameSettingOptions.G12_DREAMER]: [
		PlayerRole.dreamer,
		PlayerRole.seer,
		PlayerRole.hunter,
		PlayerRole.witch,
		PlayerRole.villager,
		PlayerRole.villager,
		PlayerRole.villager,
		PlayerRole.villager,
		PlayerRole.alpha_wolf,
		PlayerRole.werewolf,
		PlayerRole.werewolf,
		PlayerRole.werewolf
	],
	[GameSettingOptions.G12_PSYCHIC_MECHANICAL]: [
		PlayerRole.psychic,
		PlayerRole.guard,
		PlayerRole.hunter,
		PlayerRole.witch,
		PlayerRole.villager,
		PlayerRole.villager,
		PlayerRole.villager,
		PlayerRole.villager,
		PlayerRole.mechanical_wolf,
		PlayerRole.werewolf,
		PlayerRole.werewolf,
		PlayerRole.werewolf
	],
	[GameSettingOptions.G12_WOLF_BROTHERS_BLACK_MARKET]: [
		PlayerRole.seer,
		PlayerRole.witch,
		PlayerRole.guard,
		PlayerRole.black_market_dealer,
		PlayerRole.villager,
		PlayerRole.villager,
		PlayerRole.villager,
		PlayerRole.villager,
		PlayerRole.wolf_elder,
		PlayerRole.wolf_younger,
		PlayerRole.werewolf,
		PlayerRole.werewolf
	],
	[GameSettingOptions.G12_WOLF_BEAUTY_KNIGHT]: [
		PlayerRole.seer,
		PlayerRole.witch,
		PlayerRole.guard,
		PlayerRole.knight,
		PlayerRole.villager,
		PlayerRole.villager,
		PlayerRole.villager,
		PlayerRole.villager,
		PlayerRole.wolf_beauty,
		PlayerRole.werewolf,
		PlayerRole.werewolf,
		PlayerRole.werewolf
	],
	[GameSettingOptions.G12_GARGOYLE_GRAVEKEEPER]: [
		PlayerRole.seer,
		PlayerRole.witch,
		PlayerRole.hunter,
		PlayerRole.gravekeeper,
		PlayerRole.villager,
		PlayerRole.villager,
		PlayerRole.villager,
		PlayerRole.villager,
		PlayerRole.gargoyle,
		PlayerRole.werewolf,
		PlayerRole.werewolf,
		PlayerRole.werewolf
	],
	[GameSettingOptions.G12_BLOOD_MOON_DEMON_HUNTER]: [
		PlayerRole.seer,
		PlayerRole.witch,
		PlayerRole.demon_hunter,
		PlayerRole.moron,
		PlayerRole.villager,
		PlayerRole.villager,
		PlayerRole.villager,
		PlayerRole.villager,
		PlayerRole.blood_moon_herald,
		PlayerRole.werewolf,
		PlayerRole.werewolf,
		PlayerRole.werewolf
	],
	[GameSettingOptions.G12_ALPHA_WOLF_MAGICIAN]: [
		PlayerRole.seer,
		PlayerRole.witch,
		PlayerRole.hunter,
		PlayerRole.magician,
		PlayerRole.villager,
		PlayerRole.villager,
		PlayerRole.villager,
		PlayerRole.villager,
		PlayerRole.alpha_wolf,
		PlayerRole.werewolf,
		PlayerRole.werewolf,
		PlayerRole.werewolf
	],
	[GameSettingOptions.G12_WRAITH_KNIGHT]: [
		PlayerRole.seer,
		PlayerRole.witch,
		PlayerRole.hunter,
		PlayerRole.guard,
		PlayerRole.villager,
		PlayerRole.villager,
		PlayerRole.villager,
		PlayerRole.villager,
		PlayerRole.wraith_knight,
		PlayerRole.werewolf,
		PlayerRole.werewolf,
		PlayerRole.werewolf
	],
	[GameSettingOptions.G12_SNOW_WOLF_ALPHA_KNIGHT]: [
		PlayerRole.seer,
		PlayerRole.witch,
		PlayerRole.hunter,
		PlayerRole.knight,
		PlayerRole.villager,
		PlayerRole.villager,
		PlayerRole.villager,
		PlayerRole.villager,
		PlayerRole.alpha_wolf,
		PlayerRole.snow_wolf,
		PlayerRole.werewolf,
		PlayerRole.werewolf
	]
};
const FIXED_ONLY_ROLES = [
	PlayerRole.nightmare,
	PlayerRole.dreamer,
	PlayerRole.psychic,
	PlayerRole.mechanical_wolf
];
const PlayerNames = [
	"Alice",
	"Bob",
	"Charlie",
	"David",
	"Eve",
	"Frank",
	"Grace",
	"Henry",
	"Iris",
	"Jason",
	"Kate",
	"Liam",
	"Matthew",
	"Nathan",
	"Ocean",
	"Paul",
	"Quinn",
	"Rachel",
	"Sam",
	"Tim",
	"Uma",
	"Victor",
	"Wendy",
	"Xavier",
	"Yara",
	"Zach"
];
const LANGUAGE_ENUM_VALUES = [
	"zh",
	"en",
	"fr",
	"ar",
	"es",
	"ja",
	"tw",
	"ko"
];
const PLAYER_ROLE_ENUM_VALUES = [
	"seer",
	"witch",
	"villager",
	"werewolf",
	"hunter",
	"gravekeeper",
	"gargoyle",
	"snow_wolf",
	"alpha_wolf",
	"knight",
	"demon_hunter",
	"guard",
	"black_market_dealer",
	"lucky_one",
	"wraith_knight",
	"blood_moon_herald",
	"wolf_beauty",
	"magician",
	"moron",
	"wolf_elder",
	"wolf_younger",
	"nightmare",
	"dreamer",
	"psychic",
	"mechanical_wolf"
];
let NightPhaseKey = /* @__PURE__ */ function(NightPhaseKey$1) {
	NightPhaseKey$1["nightmare"] = "nightmare";
	NightPhaseKey$1["magician"] = "magician";
	NightPhaseKey$1["black_market_dealer"] = "black_market_dealer";
	NightPhaseKey$1["gargoyle"] = "gargoyle";
	NightPhaseKey$1["dreamer"] = "dreamer";
	NightPhaseKey$1["mechanical_wolf"] = "mechanical_wolf";
	NightPhaseKey$1["psychic"] = "psychic";
	NightPhaseKey$1["evil_faction"] = "evil_faction";
	NightPhaseKey$1["guard"] = "guard";
	NightPhaseKey$1["witch"] = "witch";
	NightPhaseKey$1["seer"] = "seer";
	NightPhaseKey$1["hunter"] = "hunter";
	NightPhaseKey$1["knight"] = "knight";
	NightPhaseKey$1["gravekeeper"] = "gravekeeper";
	NightPhaseKey$1["demon_hunter"] = "demon_hunter";
	NightPhaseKey$1["lucky_one"] = "lucky_one";
	return NightPhaseKey$1;
}({});
const ROLE_EMOJIS = {
	seer: "🔮",
	witch: "🧙‍♀️",
	hunter: "🏹",
	villager: "👥",
	werewolf: "🐺",
	gargoyle: "🗿",
	snow_wolf: "❄️",
	alpha_wolf: "👑",
	wraith_knight: "💀",
	blood_moon_herald: "🩸",
	wolf_beauty: "🌹",
	wolf_elder: "🌕",
	wolf_younger: "🐾",
	magician: "🪄",
	knight: "⚔️",
	guard: "🛡️",
	demon_hunter: "👺",
	gravekeeper: "⚰️",
	black_market_dealer: "💰",
	lucky_one: "🍀",
	moron: "🃏",
	nightmare: "🌑",
	dreamer: "🌙",
	psychic: "👁️",
	mechanical_wolf: "⚙️"
};
const FACTION_EMOJIS = {
	good: "🛡️",
	evil: "🐺"
};
const SUBPHASE_EMOJIS = {
	round_night_start: "🌙",
	round_magician: "🪄",
	round_black_market_dealer: "💰",
	round_gargoyle: "🗿",
	round_nightmare: "🌑",
	round_dreamer: "🌙",
	round_mechanical_wolf: "⚙️",
	round_mechanical_wolf_chase: "⚙️",
	round_psychic: "👁️",
	round_wolf_younger: "🐾",
	round_werewolf_discuss: "🐺",
	round_werewolf_discuss_2: "🐺",
	round_werewolf_kill: "🔪",
	round_wolf_beauty_charm: "🌹",
	round_guard: "🛡️",
	round_witch_action: "🧙‍♀️",
	round_seer_verify: "🔮",
	round_hunter_action: "🏹",
	round_knight: "⚔️",
	round_gravekeeper: "⚰️",
	round_demon_hunter: "👺",
	round_lucky_one: "🍀",
	round_night_complete: "🌅",
	round_day_start: "☀️",
	round_round_speech: "💬",
	round_vote: "🗳️",
	round_tied_round_speech: "⚖️",
	round_tied_vote: "🔄",
	round_day_complete: "🌆",
	round_sheriff_nominate: "🎖️",
	round_sheriff_campaign: "🎖️",
	round_sheriff_vote: "🗳️",
	round_sheriff_tied_campaign: "🎖️",
	round_sheriff_tied_vote: "🗳️"
};
const EVIL_ROLE_POOL = [
	PlayerRole.alpha_wolf,
	PlayerRole.snow_wolf,
	PlayerRole.wraith_knight,
	PlayerRole.gargoyle,
	PlayerRole.blood_moon_herald,
	PlayerRole.wolf_beauty,
	PlayerRole.werewolf
];
const GOOD_ROLE_POOL = [
	PlayerRole.seer,
	PlayerRole.witch,
	PlayerRole.hunter,
	PlayerRole.knight,
	PlayerRole.gravekeeper,
	PlayerRole.black_market_dealer,
	PlayerRole.demon_hunter,
	PlayerRole.guard,
	PlayerRole.magician,
	PlayerRole.moron,
	PlayerRole.villager
];
const IMPLEMENTED_GAME_ROLES = [
	...GOOD_ROLE_POOL,
	PlayerRole.dreamer,
	PlayerRole.psychic,
	PlayerRole.nightmare,
	...EVIL_ROLE_POOL,
	PlayerRole.wolf_elder,
	PlayerRole.wolf_younger,
	PlayerRole.mechanical_wolf
];
const CUSTOM_GAME_ELIGIBLE_ROLES = IMPLEMENTED_GAME_ROLES.filter((role) => role !== PlayerRole.mechanical_wolf);
const BRAWL_ELIGIBLE_ROLES = CUSTOM_GAME_ELIGIBLE_ROLES;
const ULTIMATE_TRIAL_FIXED_GOOD_ROLES = [
	PlayerRole.seer,
	PlayerRole.witch,
	PlayerRole.hunter
];
function getBrawlEligibleRoles(includeWolfSiblings = true) {
	return BRAWL_ELIGIBLE_ROLES.filter((role) => includeWolfSiblings || role !== PlayerRole.wolf_elder && role !== PlayerRole.wolf_younger);
}
const ULTIMATE_TRIAL_EVIL_SPECIAL_POOL = [...BRAWL_ELIGIBLE_ROLES.filter((role) => RoleFaction[role] === "evil" && role !== PlayerRole.werewolf && role !== PlayerRole.wolf_elder && role !== PlayerRole.wolf_younger).map((role) => [role]), [PlayerRole.wolf_elder, PlayerRole.wolf_younger]];
const ULTIMATE_TRIAL_GOOD_RANDOM_POOL = BRAWL_ELIGIBLE_ROLES.filter((role) => RoleFaction[role] === "good" && role !== PlayerRole.villager && !ULTIMATE_TRIAL_FIXED_GOOD_ROLES.includes(role));
const BENCHMARK_DISPLAYED_MODELES = {
	D6_1SR1WT_2WW_2VG: [
		"openai/gpt-5",
		"google/gemini-3-pro-preview",
		"google/gemini-2.5-pro",
		"anthropic/claude-sonnet-4.5",
		"x-ai/grok-4",
		"deepseek/deepseek-v3.2-exp"
	],
	D9_1HT1SR1WT_3WW_3VG: [
		"openai/gpt-5",
		"google/gemini-2.5-pro",
		"anthropic/claude-sonnet-4.5",
		"x-ai/grok-4",
		"deepseek/deepseek-v3.2-exp"
	],
	D10_ULTIMATE_TRIAL: [
		"openai/gpt-5.2-pro",
		"google/gemini-3-pro-preview",
		"google/gemini-3-flash-preview",
		"anthropic/claude-opus-4.5",
		"x-ai/grok-4",
		"z-ai/glm-4.7",
		"z-ai/glm-5",
		"moonshotai/kimi-k2.5"
	]
};
const BENCHMARK_RANKING_MODELES = {
	D6_1SR1WT_2WW_2VG: [
		"openai/gpt-5",
		"google/gemini-3-pro-preview",
		"google/gemini-2.5-pro",
		"anthropic/claude-sonnet-4.5",
		"x-ai/grok-4",
		"deepseek/deepseek-v3.2-exp"
	],
	D9_1HT1SR1WT_3WW_3VG: [
		"openai/gpt-5",
		"google/gemini-2.5-pro",
		"anthropic/claude-sonnet-4.5",
		"x-ai/grok-4",
		"deepseek/deepseek-v3.2-exp"
	],
	D10_ULTIMATE_TRIAL: [
		"openai/gpt-5.2-pro",
		"google/gemini-3-pro-preview",
		"anthropic/claude-opus-4.5",
		"x-ai/grok-4",
		"z-ai/glm-4.7"
	]
};
const INTERNALCOMPARISON_DISPLAYED_MODELES = {
	"openai": ["openai/gpt-5.2-pro", "openai/gpt-5-pro"],
	"google": ["google/gemini-3-pro-preview", "google/gemini-2.5-pro"],
	"anthropic": ["anthropic/claude-opus-4.5", "anthropic/claude-opus-4.1"],
	"x-ai": ["x-ai/grok-4", "x-ai/grok-3"],
	"z-ai": ["z-ai/glm-4.7", "z-ai/glm-4.6"],
	"moonshotai": ["moonshotai/kimi-k2.5", "moonshotai/kimi-k2-thinking"]
};
const COMPANY_DISPLAY_NAMES = {
	"openai": "OpenAI",
	"google": "Google",
	"anthropic": "Anthropic",
	"x-ai": "xAI",
	"deepseek": "DeepSeek",
	"meta": "Meta",
	"z-ai": "Z.ai",
	"moonshotai": "MoonshotAI"
};
const AI_PARAMS = {
	temperature: .6,
	presencePenalty: .7,
	frequencyPenalty: .5,
	max_reasoning_tokens: 5120
};
const ROOM_NAME_MAX_LENGTH = 60;

//#endregion
//#region src/roster/codec.ts
function getBrawlPlayerCount$1(setting) {
	return readSettingMode(setting).symbolicPlayerCount;
}
function isBrawlGameSetting$1(setting) {
	return getBrawlPlayerCount$1(setting) !== null;
}
function isFixedGameSetting(gameSetting) {
	return Object.prototype.hasOwnProperty.call(FIXED_GAME_ROSTERS, gameSetting);
}
function getFixedRosterCode(gameSetting) {
	if (!isFixedGameSetting(gameSetting)) return null;
	const roster = FIXED_GAME_ROSTERS[gameSetting];
	const roleCounts = {};
	let villagers = 0;
	for (const role of roster) if (role === PlayerRole.villager) villagers++;
	else roleCounts[role] = (roleCounts[role] ?? 0) + 1;
	return checkboxSetting("game", roleCounts, villagers);
}
function roleCodeSetting(gameType, roleCodeString) {
	const codeToRole = {};
	for (const [role, code] of Object.entries(RoleCode)) codeToRole[code] = role;
	const roleCounts = /* @__PURE__ */ new Map();
	const regex = /(\d+)([A-Z]{2})/g;
	let match;
	let totalPlayers = 0;
	while ((match = regex.exec(roleCodeString)) !== null) {
		const [, countStr, code] = match;
		const count = Number.parseInt(countStr, 10);
		const role = codeToRole[code];
		if (!role) throw new Error(`未知的角色代码: ${code}`);
		roleCounts.set(role, (roleCounts.get(role) || 0) + count);
		totalPlayers += count;
	}
	if ((roleCounts.get("magician") ?? 0) > 1) throw new Error("每局最多允许一名魔术师");
	const goodRoles = [];
	const evilRoles = [];
	let villagerCount = 0;
	for (const [role, count] of roleCounts) {
		const faction = RoleFaction[role];
		if (role === PlayerRole.villager) villagerCount = count;
		else if (faction === "good") for (let i = 0; i < count; i++) goodRoles.push(role);
		else if (faction === "evil") for (let i = 0; i < count; i++) evilRoles.push(role);
	}
	if (goodRoles.length === 0) throw new Error("必须有好人阵营特殊角色2");
	if (evilRoles.length === 0) throw new Error("必须有狼人阵营角色");
	if (villagerCount === 0) throw new Error("必须有村民角色");
	if (evilRoles.filter((role) => role === PlayerRole.werewolf).length + evilRoles.filter((role) => role === PlayerRole.snow_wolf).length + evilRoles.filter((role) => role === PlayerRole.alpha_wolf).length >= villagerCount + goodRoles.length) throw new Error("狼人数量不能大于或等于村民和好人特殊角色的总和");
	const goodRoleCounts = countAndSortRoles(goodRoles);
	const evilRoleCounts = countAndSortRoles(evilRoles);
	const goodSpecialPart = goodRoleCounts.map(([code, count]) => `${count}${code}`).join("");
	const evilPart = evilRoleCounts.map(([code, count]) => `${count}${code}`).join("");
	return `${gameType === "data" ? "D" : "G"}${totalPlayers}_${goodSpecialPart}_${evilPart}_${villagerCount}${RoleCode[PlayerRole.villager]}`;
}
function countAndSortRoles(roles) {
	const roleCountMap = /* @__PURE__ */ new Map();
	for (const role of roles) {
		const code = RoleCode[role];
		roleCountMap.set(code, (roleCountMap.get(code) || 0) + 1);
	}
	return Array.from(roleCountMap.entries()).sort(([codeA], [codeB]) => codeA.localeCompare(codeB));
}
function readCheckboxRoleFaction(roleKey) {
	return RoleFaction[roleKey] || "evil";
}
function checkboxSetting(gameType, selectedRoles, villagerCount) {
	if ((selectedRoles[PlayerRole.magician] ?? 0) > 1) throw new Error("每局最多允许一名魔术师");
	const goodRoles = [];
	const evilRoles = [];
	let actualVillagerCount = villagerCount;
	for (const [roleKey, count] of Object.entries(selectedRoles)) if (count > 0) {
		const role = roleKey;
		if (role === PlayerRole.villager) actualVillagerCount += count;
		else {
			const faction = readCheckboxRoleFaction(roleKey);
			if (faction === "good") for (let i = 0; i < count; i++) goodRoles.push(role);
			else if (faction === "evil") for (let i = 0; i < count; i++) evilRoles.push(role);
		}
	}
	const totalPlayers = goodRoles.length + evilRoles.length + actualVillagerCount;
	const goodRoleCounts = countAndSortRoles(goodRoles);
	const evilRoleCounts = countAndSortRoles(evilRoles);
	const goodSpecialPart = goodRoleCounts.map(([code, count]) => `${count}${code}`).join("");
	const evilPart = evilRoleCounts.map(([code, count]) => `${count}${code}`).join("");
	return `${gameType === "data" ? "D" : "G"}${totalPlayers}_${goodSpecialPart}_${evilPart}_${actualVillagerCount}${RoleCode[PlayerRole.villager]}`;
}
const HUMAN_VS_AI_DISABLED_ROLES = [
	"gargoyle",
	"mechanical_wolf",
	"nightmare",
	"wolf_elder",
	"wolf_younger"
];
const disabledRoles = new Set(HUMAN_VS_AI_DISABLED_ROLES);
function humanVsAiRoleAllowed(role) {
	return !disabledRoles.has(role);
}
const CUSTOM_ROSTER_FIXED_ROLES = [
	"seer",
	"witch",
	"hunter"
];
const CUSTOM_ROSTER_WOLF_PAIR = "wolf_siblings";
const CUSTOM_ROSTER_EXTRA_ROLES = CUSTOM_GAME_ELIGIBLE_ROLES.filter((role) => RoleFaction[role] === "good" && role !== "villager" && !CUSTOM_ROSTER_FIXED_ROLES.includes(role));
const CUSTOM_ROSTER_WOLF_CATALOG = [...IMPLEMENTED_GAME_ROLES.filter((role) => RoleFaction[role] === "evil" && role !== "werewolf" && role !== "wolf_elder" && role !== "wolf_younger"), CUSTOM_ROSTER_WOLF_PAIR];
const CUSTOM_ROSTER_WOLF_CHOICES = CUSTOM_ROSTER_WOLF_CATALOG.filter((role) => role === CUSTOM_ROSTER_WOLF_PAIR || CUSTOM_GAME_ELIGIBLE_ROLES.includes(role));
function customRosterRoles(playerCount, extraRole, wolfChoice) {
	const wolves = wolfChoice === CUSTOM_ROSTER_WOLF_PAIR ? ["wolf_elder", "wolf_younger"] : wolfChoice ? [wolfChoice] : [];
	return [
		...CUSTOM_ROSTER_FIXED_ROLES,
		...extraRole ? [extraRole] : [],
		...Array.from({ length: playerCount === 10 ? 3 : 4 }, () => "villager"),
		...wolves,
		...Array.from({ length: (playerCount === 10 ? 3 : 4) - wolves.length }, () => "werewolf")
	];
}
function buildCustomRosterSetting(playerCount, extraRole, wolfChoice) {
	if (!CUSTOM_ROSTER_EXTRA_ROLES.includes(extraRole) || !CUSTOM_ROSTER_WOLF_CHOICES.includes(wolfChoice)) throw new Error("invalid_game_setting");
	const counts = {};
	for (const role of customRosterRoles(playerCount, extraRole, wolfChoice)) counts[role] = (counts[role] ?? 0) + 1;
	const villagers = counts.villager;
	delete counts.villager;
	return writeSetting({
		kind: "append-roster-mode",
		setting: checkboxSetting("game", counts, villagers),
		suffix: "U"
	});
}
const roleByCode$2 = new Map(Object.entries(RoleCode).map(([role, code]) => [code, role]));
const retainedPresetCodes = new Set([
	GameSettingOptions.G6_1SR1WT_2WW_2VG,
	GameSettingOptions.G9_1HT1SR1WT_3WW_3VG,
	"G9_1HT1SR1WT_1AW2WW_3VG",
	...Object.keys(FIXED_GAME_ROSTERS).filter((setting) => !setting.includes("SNOW_WOLF_ALPHA")).map((setting) => getFixedRosterCode(setting))
]);
function strictRoster(gameSetting, allowWolfSiblingPreview = false) {
	const suffix = gameSetting.match(/^(.*?)(?:-([UWCBRS]))?$/);
	if (!suffix) return null;
	const base = suffix[1];
	const mode = {
		U: "custom",
		W: "watch",
		C: "comparison",
		B: "byom",
		R: "random"
	}[suffix[2]] ?? null;
	if (base.startsWith("D") && mode !== "random") return null;
	if (isFixedGameSetting(base)) {
		if (mode || base.includes("SNOW_WOLF_ALPHA")) return null;
		const parsed = strictRoster(getFixedRosterCode(base));
		return parsed ? {
			...parsed,
			preset: base
		} : null;
	}
	const match = (allowWolfSiblingPreview ? base.replace("1BP", "1WE1WY") : base).match(/^[GD]([1-9]\d*)_([0-9A-Z]+)_([0-9A-Z]+)_([1-9]\d*)VG$/);
	if (!match) return null;
	const playerCount = Number(match[1]);
	const villagers = Number(match[4]);
	if (!Number.isSafeInteger(playerCount) || playerCount > 12 || !Number.isSafeInteger(villagers) || villagers >= playerCount) return null;
	const counts = { villager: villagers };
	for (const [section, faction] of [[match[2], "good"], [match[3], "evil"]]) {
		const pattern = /([1-9]\d*)([A-Z]{2})/g;
		let cursor = 0;
		let token;
		while ((token = pattern.exec(section)) !== null) {
			const amount = Number(token[1]);
			const role = roleByCode$2.get(token[2]);
			if (token.index !== cursor || !role || role === "villager" || role in counts || RoleFaction[role] !== faction || !Number.isSafeInteger(amount) || amount > playerCount) return null;
			counts[role] = (counts[role] ?? 0) + amount;
			cursor = pattern.lastIndex;
		}
		if (cursor !== section.length) return null;
	}
	if (Object.values(counts).reduce((sum, count) => sum + count, 0) !== playerCount || Object.entries(counts).some(([role, count]) => role !== "villager" && role !== "werewolf" && count !== 1) || (counts.wolf_elder ?? 0) !== (counts.wolf_younger ?? 0) || counts.snow_wolf && counts.alpha_wolf) return null;
	const evilCount = Object.entries(counts).reduce((sum, [role, count]) => sum + (RoleFaction[role] === "evil" ? count : 0), 0);
	if (evilCount < 1 || evilCount >= playerCount - evilCount) return null;
	const orderedCounts = { ...counts };
	delete orderedCounts.villager;
	const canonical = checkboxSetting("game", orderedCounts, villagers);
	return {
		playerCount,
		roleCounts: counts,
		mode,
		preset: !mode && retainedPresetCodes.has(canonical) ? canonical : null
	};
}
function customRosterAllowed(parsed) {
	const { playerCount, roleCounts } = parsed;
	if (playerCount !== 10 && playerCount !== 12) return false;
	const extraRoles = Object.keys(roleCounts).filter((role) => RoleFaction[role] === "good" && role !== "villager" && !CUSTOM_ROSTER_FIXED_ROLES.includes(role));
	const specialWolves = Object.keys(roleCounts).filter((role) => RoleFaction[role] === "evil" && role !== "werewolf");
	const wolfChoice = specialWolves.length === 2 && specialWolves.includes("wolf_elder") && specialWolves.includes("wolf_younger") ? CUSTOM_ROSTER_WOLF_PAIR : specialWolves.length === 1 ? specialWolves[0] : void 0;
	if (!CUSTOM_ROSTER_FIXED_ROLES.every((role) => roleCounts[role] === 1) || extraRoles.length !== 1 || !CUSTOM_ROSTER_EXTRA_ROLES.includes(extraRoles[0]) || !wolfChoice || !CUSTOM_ROSTER_WOLF_CHOICES.includes(wolfChoice)) return false;
	const expected = {};
	for (const role of customRosterRoles(playerCount, extraRoles[0], wolfChoice)) expected[role] = (expected[role] ?? 0) + 1;
	return Object.keys(expected).length === Object.keys(roleCounts).length && Object.entries(expected).every(([role, count]) => roleCounts[role] === count);
}
function newGameAllowed(gameSetting, options = {}) {
	if (isBrawlGameSetting$1(gameSetting)) return !options.humanVsAi;
	const parsed = strictRoster(gameSetting, options.allowWolfSiblingPreview);
	if (!parsed) return false;
	if (options.humanVsAi) return (!parsed.mode || parsed.mode === "custom") && customRosterAllowed(parsed) && Object.keys(parsed.roleCounts).every((role) => humanVsAiRoleAllowed(role));
	if (parsed.mode === "random") return brawlRosterAllowed(parsed);
	if (parsed.mode === "custom" || parsed.mode === "watch" || parsed.mode === "comparison" || parsed.mode === "byom") return customRosterAllowed(parsed);
	if (parsed.preset) return true;
	if (Object.keys(parsed.roleCounts).some((role) => !CUSTOM_GAME_ELIGIBLE_ROLES.includes(role))) return false;
	if (options.allowWolfSiblingPreview || parsed.mode) return true;
	return customRosterAllowed(parsed);
}
function brawlRosterAllowed(parsed) {
	return customRosterAllowed(parsed) && Object.keys(parsed.roleCounts).every((role) => BRAWL_ELIGIBLE_ROLES.includes(role));
}
const freeGameSelections = [
	...Object.values(GameSettingOptions).filter((setting) => isFreeGameEligibleSetting$1(setting, "solo")),
	GameSettingOptions.HUMAN_VS_AI,
	GameSettingOptions.USER_CUSTOM_CONFIG
];
const FREE_GAME_SETTINGS_BY_PLAY_MODE = {
	solo: freeGameSelections,
	multiplayer: freeGameSelections
};
function isFreeGameEligibleSetting$1(gameSetting, _playMode, humanVsAi = false) {
	const mode = readSettingMode(gameSetting);
	if (mode.advancedBeforeS !== null || mode.playerCount === null || mode.playerCount > FREE_GAME_MAX_PLAYERS) return false;
	return newGameAllowed(gameSetting, { humanVsAi });
}
const displayRoleByCode = new Map(Object.entries(RoleCode).map(([role, code]) => [code, role]));
const legacyFixedBoardTitles = new Set([
	GameSettingOptions.G10_WOLF_BROTHERS_BLACK_MARKET,
	GameSettingOptions.G10_WOLF_BEAUTY_KNIGHT,
	GameSettingOptions.G10_GARGOYLE_GRAVEKEEPER,
	GameSettingOptions.G10_BLOOD_MOON_DEMON_HUNTER,
	GameSettingOptions.G10_ALPHA_WOLF_MAGICIAN,
	GameSettingOptions.G10_WRAITH_KNIGHT,
	GameSettingOptions.G10_SNOW_WOLF_ALPHA_KNIGHT,
	GameSettingOptions.G12_WOLF_BROTHERS_BLACK_MARKET,
	GameSettingOptions.G12_WOLF_BEAUTY_KNIGHT,
	GameSettingOptions.G12_GARGOYLE_GRAVEKEEPER,
	GameSettingOptions.G12_BLOOD_MOON_DEMON_HUNTER,
	GameSettingOptions.G12_ALPHA_WOLF_MAGICIAN,
	GameSettingOptions.G12_WRAITH_KNIGHT,
	GameSettingOptions.G12_SNOW_WOLF_ALPHA_KNIGHT
]);
const fixedRosterSettingByCode = new Map(Object.keys(FIXED_GAME_ROSTERS).map((setting) => [getFixedRosterCode(setting), setting]));
function stripGameSettingSuffix(setting) {
	return setting.replace(/-[RWCBSH]$/, "");
}
function displayStyle(setting) {
	const baseSetting = stripGameSettingSuffix(setting);
	const fixedSetting = isFixedGameSetting(baseSetting) ? baseSetting : fixedRosterSettingByCode.get(baseSetting);
	if (!fixedSetting) return "grouped";
	if (!legacyFixedBoardTitles.has(fixedSetting)) return "inline";
	return setting === fixedSetting ? "title" : "grouped";
}
function parseRoleGroup(value, faction) {
	const roles = [];
	let offset = 0;
	let total = 0;
	for (const match of value.matchAll(/(\d+)([A-Z]{2})/g)) {
		if (match.index !== offset) return null;
		const count = Number(match[1]);
		const code = match[2];
		const roleKey = displayRoleByCode.get(code);
		if (!Number.isSafeInteger(count) || count < 1 || !roleKey || roleKey === "villager" || RoleFaction[roleKey] !== faction || !Number.isSafeInteger(total + count)) return null;
		roles.push({
			roleKey,
			code,
			count,
			faction
		});
		total += count;
		offset += match[0].length;
	}
	return offset === value.length && roles.length > 0 ? {
		roles,
		count: total
	} : null;
}
function displayRoster(setting) {
	const suffixMatch = setting.match(/-([RWCBSHU])$/);
	const baseSetting = suffixMatch ? setting.slice(0, -suffixMatch[0].length) : setting;
	const match = (getFixedRosterCode(baseSetting) ?? baseSetting).match(/^([GD])([1-9]\d*)_([0-9A-Z]+)_([0-9A-Z]+)_(\d+)VG$/);
	if (!match) return null;
	const playerCount = Number(match[2]);
	const villagers = Number(match[5]);
	const good = parseRoleGroup(match[3], "good");
	const evil = parseRoleGroup(match[4], "evil");
	if (!Number.isSafeInteger(playerCount) || !Number.isSafeInteger(villagers) || villagers < 0 || !good || !evil || !Number.isSafeInteger(good.count + evil.count + villagers) || good.count + evil.count + villagers !== playerCount) return null;
	const goodRoles = [...good.roles];
	if (villagers > 0) goodRoles.push({
		roleKey: "villager",
		code: RoleCode.villager,
		count: villagers,
		faction: "good"
	});
	return {
		playerCount,
		gameType: match[1],
		isRandom: suffixMatch?.[1] === "R",
		goodRoles,
		evilRoles: evil.roles
	};
}
const profileRoleByCode = new Map(Object.entries(RoleCode).map(([role, code]) => [code, role]));
function playerConfigTokens(section) {
	return [...section.matchAll(/(\d+)([A-Z]{2})/g)].map((token) => ({
		code: token[2],
		count: Number.parseInt(token[1], 10),
		role: profileRoleByCode.get(token[2]) ?? null
	}));
}
function playerConfigGrammar(setting) {
	let magicianCount = 0;
	for (const token of setting.matchAll(/(\d+)([A-Z]{2})/g)) if (token[2] === RoleCode.magician) magicianCount += Number.parseInt(token[1], 10);
	const match = setting.match(/^([GD])(\d+)_([0-9A-Z]+)_([0-9A-Z]+)_(\d+)([A-Z]{2})(?:-[RWCB])?$/);
	const goodTokens = match ? playerConfigTokens(match[3]) : [];
	const evilTokens = match ? playerConfigTokens(match[4]) : [];
	const villagerCount = match ? Number.parseInt(match[5], 10) : null;
	const allTokens = [...goodTokens, ...evilTokens];
	return {
		match: match !== null,
		prefix: match ? match[1] : null,
		totalPlayers: match ? Number.parseInt(match[2], 10) : null,
		villagerCount,
		villagerCode: match?.[6] ?? null,
		goodTokens,
		evilTokens,
		firstUnknownCode: allTokens.find((token) => token.role === null)?.code ?? null,
		roleCount: match ? allTokens.reduce((count, token) => count + token.count, villagerCount) : null,
		magicianCount
	};
}
function wolfSiblingSection(section, faction, previewAllowed) {
	let count = 0;
	let elder = 0;
	let younger = 0;
	let preview = 0;
	let cursor = 0;
	const result = (valid) => ({
		valid,
		count,
		elder,
		younger,
		preview
	});
	for (const token of section.matchAll(/(\d+)([A-Z]{2})/g)) {
		if (token.index !== cursor) return result(false);
		cursor = token.index + token[0].length;
		const amount = Number.parseInt(token[1], 10);
		if (!Number.isSafeInteger(amount) || amount < 1) return result(false);
		const code = token[2];
		if (code === "BP") {
			if (!previewAllowed || faction !== "evil") return result(false);
			preview += amount;
			count += amount * 2;
			continue;
		}
		const role = profileRoleByCode.get(code);
		if (!role || RoleFaction[role] !== faction) return result(false);
		if (code === "WE") elder += amount;
		if (code === "WY") younger += amount;
		count += amount;
	}
	return result(cursor === section.length);
}
function wolfSiblingGrammar(setting, previewAllowed = false) {
	const hasMarker = /WE|WY|BP/.test(setting);
	const parts = setting.match(/^([GD])(\d+)_([0-9A-Z]+)_([0-9A-Z]+)_(\d+)VG(-[RWCBU])?$/);
	const prefix = parts ? parts[1] : null;
	const suffix = parts?.[6] ?? null;
	const good = parts ? wolfSiblingSection(parts[3], "good", previewAllowed) : null;
	const evil = parts ? wolfSiblingSection(parts[4], "evil", previewAllowed) : null;
	const total = parts ? Number.parseInt(parts[2], 10) : null;
	const villagers = parts ? Number.parseInt(parts[5], 10) : null;
	const compositionValid = good !== null && evil !== null && good.valid && evil.valid && Number.isSafeInteger(total) && Number.isSafeInteger(villagers) && villagers >= 1 && good.count + evil.count + villagers === total && good.elder === 0 && good.younger === 0 && good.preview === 0;
	const pairValid = compositionValid && evil.preview === 0 && evil.elder === 1 && evil.younger === 1;
	const previewValid = compositionValid && prefix === "G" && suffix === null && evil.preview === 1 && evil.elder === 0 && evil.younger === 0;
	return {
		hasMarker,
		match: parts !== null,
		prefix,
		suffix,
		good,
		evil,
		total,
		villagers,
		pairValid,
		previewValid,
		expandedPreviewSetting: previewValid ? `${prefix}${total}_${parts[3]}_${parts[4].replace("1BP", "1WE1WY")}_${parts[5]}VG` : null
	};
}

//#endregion
//#region src/roster/existing-game.ts
const legacyBrawlRoles = [
	"werewolf",
	"alpha_wolf",
	"snow_wolf",
	"wraith_knight",
	"gargoyle",
	"blood_moon_herald",
	"wolf_beauty",
	"seer",
	"witch",
	"hunter",
	"knight",
	"gravekeeper",
	"black_market_dealer",
	"demon_hunter",
	"guard",
	"magician",
	"villager",
	"moron"
];
const roleByCode$1 = new Map(Object.entries(RoleCode).map(([role, code]) => [code, role]));
function hasConcreteWolfSiblingPair(setting) {
	const roster = setting.split("-")[0];
	return /\d+WE/.test(roster) && /\d+WY/.test(roster);
}
function settingRoles(setting) {
	const rosterCode = getFixedRosterCode(setting) ?? setting;
	const present = /* @__PURE__ */ new Set();
	for (const match of rosterCode.matchAll(/(\d+)([A-Z]{2})/g)) {
		if (Number.parseInt(match[1], 10) <= 0) continue;
		const role = roleByCode$1.get(match[2]);
		if (role) present.add(role);
	}
	return [...present];
}
function readExistingGameRoster(query) {
	const wolfSiblings = query.recordedWolfSiblings ?? hasConcreteWolfSiblingPair(query.setting);
	const allowed = new Set(getBrawlEligibleRoles(true));
	const { recordedPool } = query;
	const brawlPoolRoles = !query.recordedPoolInvalidNonempty && recordedPool !== null && recordedPool.length > 0 && recordedPool.every((role) => allowed.has(role)) ? [...new Set(recordedPool)] : [...legacyBrawlRoles, ...wolfSiblings ? ["wolf_elder", "wolf_younger"] : []];
	const random = query.setting.endsWith("-R");
	const recordedNonempty = query.recordedPoolInvalidNonempty || (recordedPool?.length ?? 0) > 0;
	return {
		publicRoles: random ? [...brawlPoolRoles] : settingRoles(query.setting),
		claimableRoles: random && recordedNonempty ? [...brawlPoolRoles] : [...new Set(query.seatedRoles)],
		brawlPoolRoles,
		wolfSiblings
	};
}

//#endregion
//#region src/roster/index.ts
const violationOrder = [
	"unparseable",
	"seat_count",
	"faction_balance",
	"one_special_wolf",
	"siblings_paired",
	"role_not_unique",
	"role_not_in_mode",
	"mechanical_wolf_preset_only",
	"human_vs_ai_role",
	"mode_not_multiplayer",
	"free_game_seat_limit",
	"free_game_nightmare_options"
];
const roleByCode = new Map(Object.entries(RoleCode).map(([role, code]) => [code, role]));
const presets = Object.values(GameSettingOptions);
function roleEntry(role) {
	return {
		role,
		code: RoleCode[role],
		faction: RoleFaction[role],
		emoji: ROLE_EMOJIS[role]
	};
}
function rosterChoices(wolfSiblings = true) {
	return {
		fixed: CUSTOM_ROSTER_FIXED_ROLES,
		extraGodChoices: CUSTOM_ROSTER_EXTRA_ROLES,
		wolfChoices: wolfSiblings ? CUSTOM_ROSTER_WOLF_CHOICES : CUSTOM_ROSTER_WOLF_CHOICES.filter((role) => role !== CUSTOM_ROSTER_WOLF_PAIR),
		wolfCatalog: wolfSiblings ? CUSTOM_ROSTER_WOLF_CATALOG : CUSTOM_ROSTER_WOLF_CATALOG.filter((role) => role !== CUSTOM_ROSTER_WOLF_PAIR),
		presets,
		humanVsAiDisabledRoles: HUMAN_VS_AI_DISABLED_ROLES
	};
}
function selectionSetting(query) {
	const counts = {};
	for (const role of query.roles) counts[role] = (counts[role] ?? 0) + 1;
	const base = checkboxSetting("game", counts, 0);
	switch (query.mode) {
		case "watch": return writeSetting({
			kind: "select-mode",
			setting: base,
			mode: "W"
		});
		case "comparison": return writeSetting({
			kind: "select-mode",
			setting: base,
			mode: "C"
		});
		case "byom": return writeSetting({
			kind: "select-mode",
			setting: base,
			mode: "B"
		});
		case "custom": return writeSetting({
			kind: "append-roster-mode",
			setting: base,
			suffix: "U"
		});
		case "random": return writeSetting({
			kind: "append-roster-mode",
			setting: base,
			suffix: "R"
		});
		default: return base;
	}
}
function rawRosterEvidence(setting, preview) {
	const rawBase = setting.match(/^(.*?)(?:-([UWCBRS]))?$/)?.[1] ?? setting;
	const base = getFixedRosterCode(rawBase) ?? rawBase;
	const shape = (preview ? base.replace("1BP", "1WE1WY") : base).match(/^[GD]([1-9]\d*)_([0-9A-Z]+)_([0-9A-Z]+)_([1-9]\d*)VG$/);
	const counts = {};
	const occurrences = {};
	const amounts = [];
	let tokenCoverage = true;
	let wrongFaction = false;
	if (shape) {
		counts.villager = Number(shape[4]);
		occurrences.villager = 1;
		amounts.push(counts.villager);
		for (const [section, faction] of [[shape[2], "good"], [shape[3], "evil"]]) {
			let cursor = 0;
			for (const token of section.matchAll(/([1-9]\d*)([A-Z]{2})/g)) {
				const role = roleByCode.get(token[2]);
				const count = Number(token[1]);
				amounts.push(count);
				if (token.index !== cursor || !role || role === "villager") tokenCoverage = false;
				if (role) {
					counts[role] = (counts[role] ?? 0) + count;
					occurrences[role] = (occurrences[role] ?? 0) + 1;
					wrongFaction ||= RoleFaction[role] !== faction;
				}
				cursor = token.index + token[0].length;
			}
			if (cursor !== section.length) tokenCoverage = false;
		}
	}
	return {
		rawBase,
		shape,
		counts,
		amounts,
		occurrences,
		tokenCoverage,
		wrongFaction
	};
}
function readRoster(query) {
	if (query.kind === "existing-game") return readExistingGameRoster(query);
	if (query.kind === "catalog") return { choices: rosterChoices(query.wolfSiblings) };
	if (query.kind === "admin-batch") return { admissionAllowed: Object.values(DataSettingOptions).includes(query.setting) || newGameAllowed(query.setting) };
	const entry = query.entry ?? {};
	const setting = "setting" in query ? query.setting : selectionSetting(query);
	const preview = "allowWolfSiblingPreview" in query && query.allowWolfSiblingPreview === true;
	const mode = readSettingMode(setting);
	const parsed = strictRoster(setting, preview);
	const display = displayRoster(setting);
	const custom = parsed !== null && customRosterAllowed(parsed);
	const brawl = parsed !== null && brawlRosterAllowed(parsed);
	const admissionAllowed = newGameAllowed(setting, {
		humanVsAi: entry.humanVsAi,
		allowWolfSiblingPreview: preview
	});
	const playerCount = mode.symbolicPlayerCount ?? parsed?.playerCount ?? mode.playerCount;
	const evidence = rawRosterEvidence(setting, preview);
	const roles = Object.keys(evidence.counts);
	const counts = evidence.amounts;
	const evil = roles.reduce((total, role) => total + (RoleFaction[role] === "evil" ? evidence.counts[role] : 0), 0);
	const ordinaryEvil = roles.filter((role) => RoleFaction[role] === "evil" && role !== "werewolf");
	const pair = (evidence.counts.wolf_elder ?? 0) > 0 && (evidence.counts.wolf_younger ?? 0) > 0;
	const specialConfigurations = ordinaryEvil.length - (pair ? 1 : 0);
	const failures = /* @__PURE__ */ new Set();
	if ("roles" in query && query.roles.length !== query.playerCount) failures.add("seat_count");
	const symbolic = mode.symbolicPlayerCount !== null;
	const basePreset = parsed?.preset !== null && parsed?.preset !== void 0;
	const customMode = mode.terminalSuffix === "U" || mode.terminalSuffix === "W" || mode.terminalSuffix === "C" || mode.terminalSuffix === "B" || mode.terminalSuffix === "R";
	const templateRequired = !symbolic && (!basePreset || entry.humanVsAi === true) && !(preview && !customMode && !entry.humanVsAi);
	if (!symbolic) {
		if (!evidence.shape || !evidence.tokenCoverage) failures.add("unparseable");
		if (evidence.shape && (!Number.isSafeInteger(Number(evidence.shape[1])) || Number(evidence.shape[1]) > 12 || !Number.isSafeInteger(Number(evidence.shape[4])) || Number(evidence.shape[4]) >= Number(evidence.shape[1]) || counts.some((count) => !Number.isSafeInteger(count) || count > Number(evidence.shape[1])) || counts.reduce((total, count) => total + count, 0) !== Number(evidence.shape[1]))) failures.add("seat_count");
		if (evidence.shape && (evil < 1 || evil >= Number(evidence.shape[1]) - evil)) failures.add("faction_balance");
		if ((evidence.counts.wolf_elder ?? 0) !== (evidence.counts.wolf_younger ?? 0)) failures.add("siblings_paired");
		if (roles.some((role) => (evidence.occurrences[role] ?? 0) > 1 || role !== "villager" && role !== "werewolf" && evidence.counts[role] !== 1)) failures.add("role_not_unique");
		if (evidence.wrongFaction || mode.dataGame && parsed?.mode !== "random" || isFixedGameSetting(evidence.rawBase) && parsed === null) failures.add("role_not_in_mode");
		if (templateRequired && evidence.shape) {
			if (specialConfigurations !== 1) failures.add("one_special_wolf");
			if (playerCount !== 10 && playerCount !== 12) failures.add("seat_count");
			if (!custom) failures.add("role_not_in_mode");
		}
		if (evidence.counts.mechanical_wolf && (!basePreset || parsed?.mode === "random")) failures.add("mechanical_wolf_preset_only");
		if (evidence.counts.snow_wolf && evidence.counts.alpha_wolf) failures.add("one_special_wolf");
	}
	if (entry.humanVsAi && (symbolic || parsed?.mode && parsed.mode !== "custom")) failures.add("role_not_in_mode");
	if (entry.humanVsAi && roles.some((role) => !humanVsAiRoleAllowed(role))) failures.add("human_vs_ai_role");
	if (entry.multiplayer && (mode.terminalSuffix === "W" || mode.terminalSuffix === "C" || mode.terminalSuffix === "B")) failures.add("mode_not_multiplayer");
	if (entry.freeGame && (playerCount ?? 0) > FREE_GAME_MAX_PLAYERS) failures.add("free_game_seat_limit");
	if (!admissionAllowed && failures.size === 0) failures.add("role_not_in_mode");
	const seatedRoles = parsed ? Object.keys(parsed.roleCounts).filter((role) => role in RoleCode) : roles;
	const publicRoles = symbolic ? getBrawlEligibleRoles(entry.wolfSiblings).filter((role) => role in RoleCode) : seatedRoles;
	let evilNonGargoyleCount = 0;
	for (const token of setting.matchAll(/(\d+)([A-Z]{2})/g)) {
		const role = roleByCode.get(token[2]);
		if (role && role !== "gargoyle" && RoleFaction[role] === "evil") evilNonGargoyleCount += Number.parseInt(token[1], 10);
	}
	return {
		setting,
		playerCount,
		mode: preview ? "preview" : entry.humanVsAi ? "human_vs_ai" : symbolic ? "random" : parsed?.mode ?? (basePreset ? "preset" : "custom"),
		sheriff: entry.sheriff === true,
		violations: violationOrder.filter((violation) => failures.has(violation)),
		seats: seatedRoles.map((role) => ({
			...roleEntry(role),
			count: parsed?.roleCounts[role] ?? evidence.counts[role] ?? 0
		})),
		publicRoles,
		claimableRoles: [...publicRoles],
		freeGameEligible: query.kind === "selection" && (setting === GameSettingOptions.USER_CUSTOM_CONFIG || setting === GameSettingOptions.HUMAN_VS_AI) ? true : isFreeGameEligibleSetting$1(setting, "playMode" in query && query.playMode ? query.playMode : entry.multiplayer ? "multiplayer" : "solo", entry.humanVsAi),
		choices: rosterChoices(entry.wolfSiblings),
		admissionAllowed,
		preset: parsed?.preset ?? null,
		parsed,
		display,
		custom,
		brawl,
		displayStyle: displayStyle(setting),
		nightmareOptionsAllowed: true,
		humanVsAiRolesAllowed: parsed !== null && Object.keys(parsed.roleCounts).every((role) => humanVsAiRoleAllowed(role)),
		evilNonGargoyleCount,
		playerConfigGrammar: playerConfigGrammar(setting),
		wolfSiblingGrammar: wolfSiblingGrammar(setting, preview)
	};
}

//#endregion
//#region src/custom-roster-configuration.ts
function parseRosterSetting(gameSetting, allowWolfSiblingPreview = false) {
	return readRoster({
		kind: "selection",
		setting: gameSetting,
		allowWolfSiblingPreview
	}).parsed;
}
function isCustomRoster(parsed) {
	return customRosterAllowed(parsed);
}
function isNewGameRosterAllowed(gameSetting, options = {}) {
	return readRoster({
		kind: "new-game",
		setting: gameSetting,
		entry: { humanVsAi: options.humanVsAi },
		allowWolfSiblingPreview: options.allowWolfSiblingPreview
	}).admissionAllowed;
}
function isBrawlRoster(parsed) {
	return brawlRosterAllowed(parsed);
}

//#endregion
//#region src/env.ts
const isNode = typeof process !== "undefined" && process.versions != null && process.versions.node != null;
const IS_PRODUCTION = isNode ? process.env.MENTISS_ENV === "prod" : false;

//#endregion
//#region src/free-game-configuration.ts
function isFreeGameEligibleSetting(gameSetting, playMode, humanVsAi = false) {
	return readRoster({
		kind: "selection",
		setting: gameSetting,
		playMode,
		entry: { humanVsAi }
	}).freeGameEligible;
}

//#endregion
//#region src/free-quota.ts
const FREE_GAMES_PER_WINDOW = 1;
const FREE_GAME_QUOTA_ANCHOR_MS = Date.parse("2026-10-02T00:00:00Z");
const FREE_GAME_QUOTA_WINDOW_MS = 1440 * 60 * 1e3;
function normalizeAccountTimeZone(value) {
	if (!value || /^[+-]/.test(value)) return "UTC";
	try {
		return new Intl.DateTimeFormat("en", { timeZone: value }).resolvedOptions().timeZone;
	} catch {
		return "UTC";
	}
}
const dateFormatters = /* @__PURE__ */ new Map();
const dayStarts = /* @__PURE__ */ new Map();
function getAccountLocalDate(now = /* @__PURE__ */ new Date(), timeZone = "UTC") {
	const zone = normalizeAccountTimeZone(timeZone);
	let formatter = dateFormatters.get(zone);
	if (!formatter) {
		formatter = new Intl.DateTimeFormat("en", {
			timeZone: zone,
			year: "numeric",
			month: "2-digit",
			day: "2-digit"
		});
		dateFormatters.set(zone, formatter);
	}
	const parts = formatter.formatToParts(now);
	const part = (type) => parts.find((value) => value.type === type).value;
	return `${part("year")}-${part("month")}-${part("day")}`;
}
function addCalendarDays(date, days) {
	const [year, month, day] = date.split("-").map(Number);
	return new Date(Date.UTC(year, month - 1, day + days)).toISOString().slice(0, 10);
}
function calendarDayStart(date, timeZone) {
	const zone = normalizeAccountTimeZone(timeZone);
	const key = `${zone}:${date}`;
	const cached = dayStarts.get(key);
	if (cached !== void 0) return new Date(cached);
	const center = Date.parse(`${date}T00:00:00Z`);
	let lower = center - 2160 * 60 * 1e3;
	let upper = center + 2160 * 60 * 1e3;
	while (lower < upper) {
		const middle = Math.floor((lower + upper) / 2);
		if (getAccountLocalDate(new Date(middle), zone) < date) lower = middle + 1;
		else upper = middle;
	}
	if (dayStarts.size >= 512) dayStarts.clear();
	dayStarts.set(key, lower);
	return new Date(lower);
}
function getFreeGameQuotaWindowStart(now = /* @__PURE__ */ new Date(), timeZone = "UTC") {
	return calendarDayStart(getAccountLocalDate(now, timeZone), timeZone);
}
function getNextFreeGameQuotaReset(now = /* @__PURE__ */ new Date(), timeZone = "UTC") {
	return calendarDayStart(addCalendarDays(getAccountLocalDate(now, timeZone), 1), timeZone);
}
function getWeeklyQuotaResetStart(now = /* @__PURE__ */ new Date()) {
	const daysSinceMonday = (now.getUTCDay() + 6) % 7;
	return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - daysSinceMonday, 0, 0, 0, 0));
}
function getNextWeeklyQuotaReset(now = /* @__PURE__ */ new Date()) {
	const start = getWeeklyQuotaResetStart(now);
	return new Date(start.getTime() + 10080 * 60 * 1e3);
}

//#endregion
//#region src/game-roster-display.ts
function getGameRosterDisplayStyle(setting) {
	return readRoster({
		kind: "selection",
		setting
	}).displayStyle;
}
function parseGameRoster(setting) {
	return readRoster({
		kind: "selection",
		setting
	}).display;
}
function formatGameRosterGroups(parsed, roleName, options = {}) {
	const formatGroup = (roles) => roles.map((role) => {
		const name = roleName(role.roleKey);
		const label = options.includeEmoji ? `${ROLE_EMOJIS[role.roleKey]} ${name}` : name;
		return role.count > 1 ? `${label} ×${role.count}` : label;
	}).join(", ");
	return {
		good: formatGroup(parsed.goodRoles),
		evil: formatGroup(parsed.evilRoles)
	};
}
function formatGameRoster(parsed, roleName, options = {}) {
	const groups = formatGameRosterGroups(parsed, roleName, options);
	return `${groups.good} | ${groups.evil}`;
}
function parseGameSettingToPlayerConfig(gameSetting) {
	return readRoster({
		kind: "selection",
		setting: gameSetting
	}).display;
}
function formatGameSettingDisplay(parsed, t, options = {}) {
	return formatGameRoster(parsed, (roleKey) => translatedRoleName(roleKey, t), options);
}
function formatGameSettingGroups(parsed, t) {
	return formatGameRosterGroups(parsed, (roleKey) => translatedRoleName(roleKey, t));
}
function translatedRoleName(roleKey, t) {
	for (const key of [`role.${roleKey}`, `game.${roleKey}`]) {
		const translated = t(key);
		if (translated && translated !== key) return translated;
	}
	return roleKey.replaceAll("_", " ");
}
function formatRandomBoardDescription(t) {
	return [
		t("game.g10RandomInfoIntro"),
		"",
		t("game.g10RandomInfoEvil"),
		t("game.g10RandomInfoGood"),
		t("game.g10RandomInfoNote")
	].join("\n");
}
function formatGameSettingBoardLabel(gameSetting, _language, t) {
	if (!gameSetting) return null;
	const settingMode = readSettingMode(gameSetting);
	if (settingMode.terminalSuffix === "R" || settingMode.markers.containsTrial) return formatRandomBoardDescription(t);
	const parsed = parseGameSettingToPlayerConfig(gameSetting);
	if (parsed) return formatGameSettingDisplay(parsed, t, { includeEmoji: true });
	const translationKey = `gameSettings.${gameSetting}`;
	const translated = t(translationKey);
	return translated !== translationKey ? translated : gameSetting;
}

//#endregion
//#region src/model-comparison.ts
const JEV_COMPARISON_MODEL = "jev";
function isJevComparisonModel(modelName) {
	return modelName === JEV_COMPARISON_MODEL;
}
/** Read each Game's persisted scope, including previously created all-Seat Games. */
function readModelComparisonConfig(data) {
	if (!data || typeof data !== "object" || Array.isArray(data)) return null;
	const value = data;
	if (typeof value.modelA !== "string" || !value.modelA || typeof value.modelB !== "string" || !value.modelB) return null;
	const models = {
		modelA: value.modelA,
		modelB: value.modelB
	};
	if (value.comparisonScope === "all-seats") return {
		...models,
		scope: "all-seats"
	};
	if (typeof value.comparedPlayerId === "string" && value.comparedPlayerId) return {
		...models,
		scope: "single-seat",
		comparedPlayerId: value.comparedPlayerId
	};
	return null;
}
function isComparedSeat(config, playerId) {
	return config !== null && (config.scope === "all-seats" || config.comparedPlayerId === playerId);
}

//#endregion
//#region src/speech-emphasis.ts
const EMPHASIS_MARKERS = new Set(["*", "_"]);
const ATTENTION_MARKERS = new Set([
	"*",
	"_",
	"~"
]);
const ASCII_PUNCTUATION$1 = /* @__PURE__ */ new Set("!\"#$%&'()*+,-./:;<=>?@[\\]^_`{|}~");
const BLANK_LINE = /^[\t\r ]*$/;
const LINE_ENDING = /\r\n|\r|\n/g;
const UNICODE_PUNCTUATION = /\p{P}|\p{S}/u;
const UNICODE_WHITESPACE = /\s/;
function characterKind(character) {
	if (character === void 0 || UNICODE_WHITESPACE.test(character)) return "whitespace";
	return UNICODE_PUNCTUATION.test(character) ? "punctuation" : "other";
}
function paragraphRanges(text) {
	const ranges = [];
	let current = null;
	let lineStart = 0;
	while (lineStart <= text.length) {
		LINE_ENDING.lastIndex = lineStart;
		const lineEnding = LINE_ENDING.exec(text);
		const lineEnd = lineEnding ? lineEnding.index : text.length;
		if (BLANK_LINE.test(text.slice(lineStart, lineEnd))) current = null;
		else if (current) current.end = lineEnd;
		else {
			current = {
				start: lineStart,
				end: lineEnd
			};
			ranges.push(current);
		}
		if (!lineEnding) break;
		lineStart = lineEnding.index + lineEnding[0].length;
	}
	return ranges;
}
function delimiterRuns(text, paragraph) {
	const runs = [];
	let index = paragraph.start;
	while (index < paragraph.end) {
		const marker = text[index];
		if (marker === "\\" && index + 1 < paragraph.end && ASCII_PUNCTUATION$1.has(text[index + 1])) {
			index += 2;
			continue;
		}
		if (!EMPHASIS_MARKERS.has(marker)) {
			index += 1;
			continue;
		}
		let end = index;
		while (end < paragraph.end && text[end] === marker) end += 1;
		const before = index > paragraph.start ? text[index - 1] : void 0;
		const after = end < paragraph.end ? text[end] : void 0;
		const beforeKind = characterKind(before);
		const afterKind = characterKind(after);
		const leftFlanking = afterKind === "other" || afterKind === "punctuation" && beforeKind !== "other" || after !== void 0 && ATTENTION_MARKERS.has(after);
		const rightFlanking = beforeKind === "other" || beforeKind === "punctuation" && afterKind !== "other" || before !== void 0 && ATTENTION_MARKERS.has(before);
		runs.push({
			marker,
			start: index,
			size: end - index,
			canOpen: marker === "*" ? leftFlanking : leftFlanking && (beforeKind !== "other" || !rightFlanking),
			canClose: marker === "*" ? rightFlanking : rightFlanking && (afterKind !== "other" || !leftFlanking),
			active: true
		});
		index = end;
	}
	return runs;
}
function findOpener(runs, closerIndex) {
	const closer = runs[closerIndex];
	for (let index = closerIndex - 1; index >= 0; index -= 1) {
		const opener = runs[index];
		if (!opener.active || !opener.canOpen || opener.size === 0 || opener.marker !== closer.marker) continue;
		if ((opener.canClose || closer.canOpen) && closer.size % 3 !== 0 && (opener.size + closer.size) % 3 === 0) continue;
		return index;
	}
	return -1;
}
function matchRuns(runs, marks) {
	for (const [closerIndex, closer] of runs.entries()) while (closer.canClose && closer.size > 0) {
		const openerIndex = findOpener(runs, closerIndex);
		if (openerIndex === -1) break;
		const opener = runs[openerIndex];
		const length = opener.size > 1 && closer.size > 1 ? 2 : 1;
		const tag = length === 2 ? "strong" : "em";
		opener.size -= length;
		marks.set(opener.start + opener.size, {
			tag,
			closing: false,
			length
		});
		marks.set(closer.start, {
			tag,
			closing: true,
			length
		});
		closer.start += length;
		closer.size -= length;
		for (const run of runs.slice(openerIndex + 1, closerIndex)) run.active = false;
	}
}
function findSpeechEmphasis(text) {
	const marks = /* @__PURE__ */ new Map();
	for (const paragraph of paragraphRanges(text)) matchRuns(delimiterRuns(text, paragraph), marks);
	return marks;
}

//#endregion
//#region src/plain-speech.ts
const ASCII_PUNCTUATION = /* @__PURE__ */ new Set("!\"#$%&'()*+,-./:;<=>?@[\\]^_`{|}~");
const INDENTATION = new Set([" ", "	"]);
const LINE_BREAKS = new Set(["\n", "\r"]);
function normalizeSpeechRanges(content, ranges) {
	const valid = ranges.filter((range) => Number.isSafeInteger(range.start) && Number.isSafeInteger(range.end) && range.start >= 0 && range.end <= content.length && range.start < range.end).map((range) => ({
		start: range.start,
		end: range.end
	})).sort((left, right) => left.start - right.start);
	const merged = [];
	for (const range of valid) {
		const previous = merged.at(-1);
		if (previous && range.start <= previous.end) previous.end = Math.max(previous.end, range.end);
		else merged.push(range);
	}
	return merged;
}
function emphasisTag(mark) {
	return mark.closing ? `</${mark.tag}>` : `<${mark.tag}>`;
}
function mapOffset(offsets, offset) {
	if (offset === void 0) return void 0;
	if (!Number.isSafeInteger(offset) || offset < 0 || offset >= offsets.length) return void 0;
	return offsets[offset];
}
function shiftReference(reference, offsets) {
	const start = mapOffset(offsets, reference.placement.start);
	const end = mapOffset(offsets, reference.placement.end);
	const headerStart = mapOffset(offsets, reference.headerPlacement.start);
	const headerEnd = mapOffset(offsets, reference.headerPlacement.end);
	const accentEnd = mapOffset(offsets, reference.headerPlacement.accentEnd);
	if (start === void 0 || end === void 0 || headerStart === void 0 || headerEnd === void 0 || reference.headerPlacement.accentEnd !== void 0 && accentEnd === void 0) return null;
	return {
		...reference,
		placement: {
			start,
			end
		},
		headerPlacement: {
			start: headerStart,
			end: headerEnd,
			...accentEnd === void 0 ? {} : { accentEnd }
		}
	};
}
function lineIndentationStart(content, start) {
	let index = start;
	while (index > 0 && INDENTATION.has(content[index - 1])) index -= 1;
	return index === 0 || LINE_BREAKS.has(content[index - 1]) ? index : start;
}
function escapeNormalizedRanges(content, ranges) {
	const marks = /* @__PURE__ */ new Map();
	for (const range of ranges) for (const [offset, mark] of findSpeechEmphasis(content.slice(range.start, range.end))) marks.set(range.start + offset, mark);
	const indentationStarts = ranges.map((range) => lineIndentationStart(content, range.start));
	const offsets = Array.from({ length: content.length + 1 }, () => 0);
	let output = "";
	let rangeIndex = 0;
	let atLineStart = false;
	let index = 0;
	while (index < content.length) {
		const character = content[index];
		while (rangeIndex < ranges.length && ranges[rangeIndex].end <= index) rangeIndex++;
		const range = ranges[rangeIndex];
		offsets[index] = output.length;
		if (range && index >= indentationStarts[rangeIndex] && index < range.start) {
			index += 1;
			continue;
		}
		if (!range || index < range.start) {
			output += character;
			index += 1;
			continue;
		}
		if (index === range.start) atLineStart = true;
		if (LINE_BREAKS.has(character)) {
			atLineStart = true;
			output += character;
			index += 1;
			continue;
		}
		if (atLineStart && INDENTATION.has(character)) {
			index += 1;
			continue;
		}
		atLineStart = false;
		const mark = marks.get(index);
		if (mark) {
			for (let part = 1; part < mark.length; part += 1) offsets[index + part] = output.length;
			output += emphasisTag(mark);
			index += mark.length;
			continue;
		}
		output += ASCII_PUNCTUATION.has(character) ? `\\${character}` : character;
		index += 1;
	}
	offsets[content.length] = output.length;
	return {
		content: output,
		offsets
	};
}
function escapeSpeechText(content, ranges) {
	const normalized = normalizeSpeechRanges(content, ranges);
	if (normalized.length === 0) return {
		content,
		ranges: []
	};
	const escaped = escapeNormalizedRanges(content, normalized);
	return {
		content: escaped.content,
		ranges: normalized.map((range) => ({
			start: escaped.offsets[range.start],
			end: escaped.offsets[range.end]
		}))
	};
}
function escapeSpeechRanges(content, references) {
	const ranges = normalizeSpeechRanges(content, references.map((reference) => reference.placement));
	if (ranges.length === 0) return {
		content,
		references: [...references]
	};
	const { content: output, offsets } = escapeNormalizedRanges(content, ranges);
	return {
		content: output,
		references: references.map((reference) => shiftReference(reference, offsets)).filter((reference) => reference !== null)
	};
}

//#endregion
//#region src/realtime-transport.ts
function isRegisteredAccount(identity) {
	if (!identity) return false;
	return !identity.isAnonymous;
}
function canHoldStream(userInfo, authResolved) {
	if (!authResolved) return false;
	return isRegisteredAccount(userInfo);
}

//#endregion
//#region src/structured-journal-layout.ts
function walkStructuredJournal(journal, options) {
	const found = [];
	if (!journal || typeof journal !== "object") return found;
	const append = (entries, location) => {
		options.onGroup?.(entries, location);
		for (const entry of entries) {
			const derivedLocation = { ...location };
			found.push({
				entry,
				location: derivedLocation
			});
			if (options.onEntry?.(entry, derivedLocation) === false) return false;
		}
		return true;
	};
	if (options.includeInit !== false) {
		options.onInit?.(journal.init);
		if (!append(options.allowPartial ? journal.init?.entries ?? [] : journal.init.entries, { kind: "init" })) return found;
	}
	for (const round of options.allowPartial ? journal.rounds ?? [] : journal.rounds) {
		if (options.onRound?.(round) === false) continue;
		for (const phase of options.allowPartial ? round?.phases ?? [] : round.phases) {
			if (options.onPhase?.(phase, round) === false) continue;
			for (const subPhase of options.allowPartial ? phase?.subPhases ?? [] : phase.subPhases) {
				if (options.onSubPhase?.(subPhase, phase, round) === false) continue;
				const entries = options.allowPartial ? subPhase?.entries ?? [] : subPhase.entries;
				const location = () => ({
					kind: "round",
					phase: phase.phase,
					roundNumber: round.roundNumber,
					subPhase: subPhase.subPhase
				});
				if (options.onGroup) options.onGroup(entries, location());
				for (const entry of entries) {
					const derivedLocation = location();
					found.push({
						entry,
						location: derivedLocation
					});
					if (options.onEntry?.(entry, derivedLocation) === false) return found;
				}
			}
			options.afterPhase?.(phase, round);
		}
		options.afterRound?.(round);
	}
	if (options.includeResult) {
		options.onResult?.(journal.result);
		if (journal.result) append([journal.result], { kind: "result" });
	}
	return found;
}
function renderJournalTextSegments(segments, options = {}) {
	let rendered = "";
	if (options.endMarker !== void 0) {
		const renderedEndMarker = `\n${options.endMarker}\n`;
		for (const segment of segments) {
			if (rendered.endsWith(renderedEndMarker)) rendered = rendered.slice(0, -renderedEndMarker.length);
			options.onSegment?.(segment, rendered.length + 1);
			rendered = `${rendered}\n${segment.text}\n${options.endMarker}\n`;
		}
		return rendered;
	}
	for (const segment of segments) {
		options.onSegment?.(segment, rendered ? rendered.length + 1 : 0);
		rendered = rendered ? `${rendered}\n${segment.text}\n` : `${segment.text}\n`;
	}
	return rendered;
}

//#endregion
//#region src/tts.ts
const TTS_KEYCAP_PATTERN = /[0-9#*]\uFE0F?\u20E3/gu;
const TTS_EMOJI_PATTERN = /[\u{1F600}-\u{1F6FF}\u{1F300}-\u{1F5FF}\u{1F1E0}-\u{1F1FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{1F900}-\u{1F9FF}\u{1FA70}-\u{1FAFF}\u{2B50}\u{23F3}]/gu;
const TTS_EMOJI_TAG_PATTERN = /[\u{E0020}-\u{E007F}]/gu;
function cleanTtsText(text) {
	return text.replaceAll(/#{3,}/g, "").replaceAll("**", "").replaceAll(/\[\d+\]([\w\u4E00-\u9FA5]+)\([^)]+\)/g, "$1").replaceAll(TTS_KEYCAP_PATTERN, "").replaceAll(TTS_EMOJI_PATTERN, "").replaceAll(TTS_EMOJI_TAG_PATTERN, "").replaceAll("‍", "").replaceAll("︎", "").replaceAll("️", "").trim();
}

//#endregion
//#region src/speech-references.ts
const TEXT_LOG_SPEECH_KEYS = [
	"player.lastWordOfFirstNight",
	"player.lastWord",
	"player.sheriffCampaignSpeech",
	"speech.playerStart",
	"speech.playerStartGod",
	"tiedVoteSpeech.playerStart",
	"tiedVoteSpeech.playerStartGod",
	"werewolf.discussionPlayer"
];
const TEXT_LOG_PLAYER_VOICES = [
	"alloy",
	"ash",
	"coral",
	"echo",
	"fable",
	"onyx",
	"nova",
	"sage"
];
const TEXT_LOG_TTS_MAX_CHARS = 2e3;
const SPEECH_KEY_SET = new Set(TEXT_LOG_SPEECH_KEYS);
const SPEECH_BANNER = "#####";
function isRecord(value) {
	return value !== null && typeof value === "object" && !Array.isArray(value);
}
function isSpeechKey(value) {
	return typeof value === "string" && SPEECH_KEY_SET.has(value.trim());
}
function readSpeech(entry) {
	if (!isRecord(entry.vars) || typeof entry.vars.speech !== "string") return null;
	return entry.vars.speech.trim() || null;
}
function flattenEntries(log, requireEventSeq) {
	if (!isRecord(log.init) || !Array.isArray(log.rounds)) return null;
	try {
		const entries = walkStructuredJournal(log, {
			includeResult: true,
			onInit(init) {
				if (!isRecord(init) || !Array.isArray(init.entries)) throw new Error("Invalid structured journal init");
			},
			onRound(round) {
				if (!isRecord(round) || !Array.isArray(round.phases)) throw new Error("Invalid structured journal round");
			},
			onPhase(phase) {
				if (!isRecord(phase) || !Array.isArray(phase.subPhases)) throw new Error("Invalid structured journal phase");
			},
			onSubPhase(subPhase) {
				if (!isRecord(subPhase) || !Array.isArray(subPhase.entries)) throw new Error("Invalid structured journal subphase");
			},
			onResult(result) {
				if (result !== void 0 && !isRecord(result)) throw new Error("Invalid structured journal result");
			}
		});
		const seen = /* @__PURE__ */ new Set();
		return entries.map(({ entry, location }, flatIndex) => {
			if (!isRecord(entry)) throw new Error("Invalid structured journal entry");
			if (requireEventSeq) {
				if (!Number.isSafeInteger(entry.eventSeq) || entry.eventSeq <= 0) throw new Error("Invalid structured journal sequence");
				const eventSeq = entry.eventSeq;
				if (seen.has(eventSeq)) throw new Error("Duplicate structured journal sequence");
				seen.add(eventSeq);
			}
			const roundNumber = location.kind === "round" && Number.isSafeInteger(location.roundNumber) && location.roundNumber > 0 ? location.roundNumber : void 0;
			return {
				entry,
				flatIndex,
				...roundNumber === void 0 ? {} : { roundNumber }
			};
		});
	} catch {
		return null;
	}
}
function findStandaloneSpeechRange(text, speech) {
	const candidates = [];
	let cursor = 0;
	while (cursor <= text.length - speech.length) {
		const start = text.indexOf(speech, cursor);
		if (start === -1) break;
		const end = start + speech.length;
		const lineStart = text.lastIndexOf("\n", Math.max(0, start - 1)) + 1;
		const nextNewline = text.indexOf("\n", end);
		const lineEnd = nextNewline === -1 ? text.length : nextNewline;
		if (text.slice(lineStart, start).trim() === "" && text.slice(end, lineEnd).trim() === "") candidates.push({
			start,
			end
		});
		cursor = start + Math.max(1, speech.length);
	}
	return candidates.length === 1 ? candidates[0] : null;
}
function findSpeechPlacement(text, speech) {
	const speechRange = findStandaloneSpeechRange(text, speech);
	if (!speechRange) return null;
	let headerEnd = text.lastIndexOf("\n", Math.max(0, speechRange.start - 1)) + 1;
	while (headerEnd > 0 && /\s/u.test(text[headerEnd - 1])) headerEnd--;
	if (headerEnd === 0) return null;
	const headerStart = text.lastIndexOf("\n", headerEnd - 1) + 1;
	if (!text.slice(headerStart, headerEnd).trim()) return null;
	const iconIndex = text.slice(headerStart, headerEnd).indexOf("💬");
	const accentEnd = iconIndex === -1 ? void 0 : headerStart + iconIndex + 2;
	return {
		speech: speechRange,
		header: {
			start: headerStart,
			end: headerEnd,
			...accentEnd === void 0 ? {} : { accentEnd }
		}
	};
}
function nonBlankLines(text) {
	const lines = [];
	let lineStart = 0;
	while (lineStart <= text.length) {
		const newline = text.indexOf("\n", lineStart);
		const lineEnd = newline === -1 ? text.length : newline;
		if (text.slice(lineStart, lineEnd).trim()) lines.push({
			start: lineStart,
			end: lineEnd
		});
		if (newline === -1) break;
		lineStart = newline + 1;
	}
	return lines;
}
function isBannerLine(text, line) {
	return text.slice(line.start, line.end).trim().startsWith(SPEECH_BANNER);
}
function findSpeechBodyPlacement(text) {
	const lines = nonBlankLines(text);
	const header = lines[0];
	const footer = lines.at(-1);
	if (lines.length < 3 || !header || !footer || !isBannerLine(text, header) || !isBannerLine(text, footer)) return null;
	let start = header.end;
	let end = footer.start;
	while (start < end && /\s/u.test(text[start])) start++;
	while (end > start && /\s/u.test(text[end - 1])) end--;
	let headerEnd = header.end;
	while (headerEnd > header.start && /\s/u.test(text[headerEnd - 1])) headerEnd--;
	const iconIndex = text.slice(header.start, headerEnd).indexOf("💬");
	return {
		speech: {
			start,
			end
		},
		header: {
			start: header.start,
			end: headerEnd,
			...iconIndex === -1 ? {} : { accentEnd: header.start + iconIndex + 2 }
		}
	};
}
function getSpeaker(entry) {
	if (!isRecord(entry.vars) || !isRecord(entry.vars.player)) return {};
	const { player } = entry.vars;
	return {
		playerId: typeof player.playerId === "string" && player.playerId ? player.playerId : void 0,
		position: Number.isSafeInteger(player.position) && player.position > 0 ? player.position : void 0
	};
}
function buildVoiceMap(players) {
	if (!players) return /* @__PURE__ */ new Map();
	const sorted = players.filter((player) => player.id && Number.isSafeInteger(player.position)).slice().sort((left, right) => left.position - right.position || left.id.localeCompare(right.id));
	return new Map(sorted.map((player, index) => [player.id, TEXT_LOG_PLAYER_VOICES[index % TEXT_LOG_PLAYER_VOICES.length]]));
}
function resolveVoice(speaker, voiceMap) {
	if (speaker.playerId && voiceMap.has(speaker.playerId)) return voiceMap.get(speaker.playerId);
	if (speaker.position && speaker.position > 0) return TEXT_LOG_PLAYER_VOICES[(speaker.position - 1) % TEXT_LOG_PLAYER_VOICES.length];
	return "alloy";
}
function makeReference(input) {
	const speaker = getSpeaker(input.entry);
	return {
		id: `tts:${input.gameId}:${input.perspective}:${input.identity}`,
		key: input.key,
		speech: input.speech,
		...speaker,
		voiceName: resolveVoice(speaker, input.voiceMap),
		language: input.language,
		perspective: input.perspective,
		...input.eventSeq === void 0 ? {} : { eventSeq: input.eventSeq },
		...input.legacyOccurrence === void 0 ? {} : { legacyOccurrence: input.legacyOccurrence },
		...input.roundNumber === void 0 ? {} : { roundNumber: input.roundNumber },
		placement: input.placement,
		headerPlacement: input.headerPlacement,
		ttsEligible: input.speech.length <= TEXT_LOG_TTS_MAX_CHARS && cleanTtsText(input.speech).length > 0
	};
}
function extractV2(log, input, voiceMap) {
	const flatEntries = flattenEntries(log, true);
	if (!flatEntries || !isRecord(log.text) || !Array.isArray(log.text.segments)) return [];
	const segments = [];
	const eventToSegment = /* @__PURE__ */ new Map();
	const knownEvents = new Set(flatEntries.map(({ entry }) => entry.eventSeq));
	for (const value of log.text.segments) {
		if (!isRecord(value) || typeof value.text !== "string" || !Array.isArray(value.eventSeqs) || value.eventSeqs.length === 0) return [];
		const eventSeqs = [];
		const segmentEvents = /* @__PURE__ */ new Set();
		for (const eventSeq of value.eventSeqs) {
			if (!Number.isSafeInteger(eventSeq) || !knownEvents.has(eventSeq)) return [];
			if (segmentEvents.has(eventSeq) || eventToSegment.has(eventSeq)) return [];
			segmentEvents.add(eventSeq);
			eventSeqs.push(eventSeq);
		}
		const segment = {
			eventSeqs,
			text: value.text,
			start: 0
		};
		segments.push(segment);
		for (const eventSeq of eventSeqs) eventToSegment.set(eventSeq, segment);
	}
	const renderedMain = renderJournalTextSegments(segments, { onSegment(segment, start) {
		segment.start = start;
	} });
	if (renderedMain && !input.renderedLog.endsWith(renderedMain)) return [];
	const mainStart = renderedMain ? input.renderedLog.length - renderedMain.length : input.renderedLog.length;
	const references = [];
	for (const { entry, roundNumber } of flatEntries) {
		if (!isSpeechKey(entry.key)) continue;
		const speech = readSpeech(entry);
		if (!speech) continue;
		const eventSeq = entry.eventSeq;
		const segment = eventToSegment.get(eventSeq);
		if (!segment) continue;
		const speechPlacement = findSpeechPlacement(segment.text, speech) ?? (segment.eventSeqs.length === 1 ? findSpeechBodyPlacement(segment.text) : null);
		if (!speechPlacement) continue;
		references.push(makeReference({
			gameId: input.gameId || "unknown-game",
			perspective: input.perspective,
			identity: `v2:${eventSeq}`,
			key: entry.key.trim(),
			speech,
			entry,
			placement: {
				start: mainStart + segment.start + speechPlacement.speech.start,
				end: mainStart + segment.start + speechPlacement.speech.end
			},
			headerPlacement: {
				start: mainStart + segment.start + speechPlacement.header.start,
				end: mainStart + segment.start + speechPlacement.header.end,
				...speechPlacement.header.accentEnd === void 0 ? {} : { accentEnd: mainStart + segment.start + speechPlacement.header.accentEnd }
			},
			language: input.language || "en",
			voiceMap,
			eventSeq,
			roundNumber
		}));
	}
	return references;
}
function stripHeaderHashesWithSourceMap(source) {
	let text = "";
	const sourceOffsets = [];
	for (let index = 0; index < source.length;) {
		if (source[index] === "#") {
			let end = index;
			while (source[end] === "#") end++;
			if (end - index >= 3) {
				index = end;
				continue;
			}
		}
		text += source[index];
		sourceOffsets.push(index);
		index++;
	}
	return {
		text,
		sourceOffsets
	};
}
function allOccurrences(text, needle) {
	const result = [];
	let cursor = 0;
	while (cursor <= text.length - needle.length) {
		const found = text.indexOf(needle, cursor);
		if (found === -1) break;
		result.push(found);
		cursor = found + Math.max(1, needle.length);
	}
	return result;
}
function extractV1(log, input, voiceMap) {
	const flatEntries = flattenEntries(log, false);
	if (!flatEntries) return [];
	const candidates = [];
	for (const { entry, flatIndex, roundNumber } of flatEntries) {
		if (!isSpeechKey(entry.key)) continue;
		const speech = readSpeech(entry);
		if (!speech) continue;
		if (typeof entry.translation !== "string") return [];
		const translation = entry.translation.trim();
		const speechPlacement = findSpeechPlacement(translation, speech);
		if (!translation || !speechPlacement) return [];
		candidates.push({
			entry,
			flatIndex,
			roundNumber,
			key: entry.key.trim(),
			speech,
			translation,
			speechPlacement
		});
	}
	if (candidates.length === 0) return [];
	const normalized = stripHeaderHashesWithSourceMap(input.renderedLog);
	const groups = /* @__PURE__ */ new Map();
	for (const candidate of candidates) {
		const group = groups.get(candidate.translation) ?? [];
		group.push(candidate);
		groups.set(candidate.translation, group);
	}
	const mapped = [];
	for (const [translation, group] of groups) {
		const occurrences = allOccurrences(normalized.text, translation);
		if (occurrences.length !== group.length) return [];
		group.forEach((candidate, occurrenceIndex) => {
			const blockStart = occurrences[occurrenceIndex];
			const normalizedStart = blockStart + candidate.speechPlacement.speech.start;
			const normalizedEnd = blockStart + candidate.speechPlacement.speech.end;
			const sourceStart = normalized.sourceOffsets[normalizedStart];
			const sourceEndOffset = normalized.sourceOffsets[normalizedEnd - 1];
			if (sourceStart === void 0 || sourceEndOffset === void 0) return;
			const placement = {
				start: sourceStart,
				end: sourceEndOffset + 1
			};
			if (input.renderedLog.slice(placement.start, placement.end) !== candidate.speech) return;
			const normalizedHeaderStart = blockStart + candidate.speechPlacement.header.start;
			const normalizedHeaderEnd = blockStart + candidate.speechPlacement.header.end;
			const sourceHeaderStart = normalized.sourceOffsets[normalizedHeaderStart];
			const sourceHeaderEndOffset = normalized.sourceOffsets[normalizedHeaderEnd - 1];
			if (sourceHeaderStart === void 0 || sourceHeaderEndOffset === void 0) return;
			const normalizedAccentEnd = candidate.speechPlacement.header.accentEnd;
			const sourceAccentEndOffset = normalizedAccentEnd === void 0 ? void 0 : normalized.sourceOffsets[blockStart + normalizedAccentEnd - 1];
			if (normalizedAccentEnd !== void 0 && sourceAccentEndOffset === void 0) return;
			mapped.push({
				candidate,
				placement,
				headerPlacement: {
					start: sourceHeaderStart,
					end: sourceHeaderEndOffset + 1,
					...sourceAccentEndOffset === void 0 ? {} : { accentEnd: sourceAccentEndOffset + 1 }
				}
			});
		});
		if (mapped.filter((item) => group.includes(item.candidate)).length !== group.length) return [];
	}
	mapped.sort((left, right) => left.candidate.flatIndex - right.candidate.flatIndex);
	for (let index = 1; index < mapped.length; index++) if (mapped[index].placement.start <= mapped[index - 1].placement.end) return [];
	return mapped.map(({ candidate, placement, headerPlacement }, legacyOccurrence) => makeReference({
		gameId: input.gameId || "unknown-game",
		perspective: input.perspective,
		identity: `v1:${candidate.flatIndex}`,
		key: candidate.key,
		speech: candidate.speech,
		entry: candidate.entry,
		placement,
		headerPlacement,
		language: input.language || "en",
		voiceMap,
		legacyOccurrence,
		roundNumber: candidate.roundNumber
	}));
}
function extractTextLogSpeechReferences(input) {
	if (!isRecord(input.structuredLog) || typeof input.renderedLog !== "string") return [];
	const voiceMap = buildVoiceMap(input.players);
	if (input.structuredLog.schemaVersion === 2) return extractV2(input.structuredLog, input, voiceMap);
	if ("schemaVersion" in input.structuredLog) return [];
	return extractV1(input.structuredLog, input, voiceMap);
}
function extractTextLogSpeechRanges(input) {
	if (typeof input.renderedLog !== "string") return [];
	return normalizeSpeechRanges(input.renderedLog, extractTextLogSpeechReferences(input).map((reference) => reference.placement));
}
function rebaseSpeechReferences(references, sourceStart, sourceEnd) {
	return references.filter((reference) => reference.placement.start >= sourceStart && reference.placement.end <= sourceEnd && reference.headerPlacement.start >= sourceStart && reference.headerPlacement.end <= sourceEnd).map((reference) => ({
		...reference,
		placement: {
			start: reference.placement.start - sourceStart,
			end: reference.placement.end - sourceStart
		},
		headerPlacement: {
			start: reference.headerPlacement.start - sourceStart,
			end: reference.headerPlacement.end - sourceStart,
			...reference.headerPlacement.accentEnd === void 0 ? {} : { accentEnd: reference.headerPlacement.accentEnd - sourceStart }
		}
	}));
}

//#endregion
//#region src/steam.ts
const STEAM_WEB_API_TICKET_IDENTITY = "mentiss";
/**
* The product token that the Steam Game App adds to Chromium's user agent
* (`MentissSteam/<version>`), so the API can tell its sessions apart.
*/
const STEAM_CLIENT_USER_AGENT_PRODUCT = "MentissSteam";

//#endregion
//#region src/structured-log-delta.ts
function isSequenced(value) {
	return typeof value === "object" && value !== null && Number.isFinite(value.eventSeq);
}
function walk(tree) {
	return walkStructuredJournal(tree, {
		includeResult: false,
		allowPartial: true
	}).filter((located) => isSequenced(located.entry)).sort((left, right) => left.entry.eventSeq - right.entry.eventSeq);
}
function structuredLogRevision(tree) {
	let highest = 0;
	for (const { entry } of walk(tree)) highest = Math.max(highest, entry.eventSeq);
	return highest;
}
function extractStructuredLogDelta(tree, since) {
	const cursor = Number.isFinite(since) && since > 0 ? since : 0;
	const all = walk(tree);
	return {
		entries: all.filter(({ entry }) => entry.eventSeq > cursor),
		revision: all.reduce((highest, { entry }) => Math.max(highest, entry.eventSeq), 0),
		since: cursor
	};
}
function emptyTree() {
	return {
		init: { entries: [] },
		rounds: []
	};
}
function upsertSubPhase(tree, location) {
	const rounds = tree.rounds ??= [];
	let round = rounds.find((r) => r.roundNumber === location.roundNumber);
	if (!round) {
		round = {
			phases: [],
			roundNumber: location.roundNumber
		};
		rounds.push(round);
		rounds.sort((left, right) => left.roundNumber - right.roundNumber);
	}
	round.phases ??= [];
	let phase = round.phases.find((p) => p.phase === location.phase);
	if (!phase) {
		phase = {
			phase: location.phase,
			subPhases: []
		};
		round.phases.push(phase);
	}
	phase.subPhases ??= [];
	let subPhase = phase.subPhases.find((s) => s.subPhase === location.subPhase);
	if (!subPhase) {
		subPhase = {
			entries: [],
			subPhase: location.subPhase
		};
		phase.subPhases.push(subPhase);
	}
	return subPhase;
}
function mergeStructuredLogDelta(cached, delta) {
	const next = cached && typeof cached === "object" ? {
		...cached,
		init: { entries: [...cached.init?.entries ?? []] },
		rounds: (cached.rounds ?? []).map((round) => ({
			...round,
			phases: (round.phases ?? []).map((phase) => ({
				...phase,
				subPhases: (phase.subPhases ?? []).map((subPhase) => ({
					...subPhase,
					entries: [...subPhase.entries ?? []]
				}))
			}))
		}))
	} : emptyTree();
	const place = (entries, entry) => {
		const existing = entries.findIndex((e) => e.eventSeq === entry.eventSeq);
		if (existing === -1) entries.push(entry);
		else entries[existing] = entry;
		entries.sort((left, right) => left.eventSeq - right.eventSeq);
	};
	for (const { entry, location } of delta.entries) {
		if (location.kind === "init") {
			next.init ??= { entries: [] };
			place(next.init.entries, entry);
			continue;
		}
		place(upsertSubPhase(next, location).entries, entry);
	}
	return next;
}

//#endregion
//#region src/util.ts
function shuffleArray(array) {
	const shuffled = [...array];
	for (let i = shuffled.length - 1; i > 0; i--) {
		const j = Math.floor(Math.random() * (i + 1));
		[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
	}
	return shuffled;
}
function getBrawlPlayerCount(gameSetting) {
	return readSettingMode(gameSetting).symbolicPlayerCount;
}
function isBrawlGameSetting(gameSetting) {
	return getBrawlPlayerCount(gameSetting) !== null;
}
function generateSettingForUltimateTrial(gameType, playerCount, includeWolfSiblings = true, random = Math.random) {
	if (playerCount !== 10 && playerCount !== 12) throw new Error("invalid_game_setting");
	const outcomes = includeWolfSiblings ? ULTIMATE_TRIAL_EVIL_SPECIAL_POOL : ULTIMATE_TRIAL_EVIL_SPECIAL_POOL.filter((roles) => roles.length === 1);
	const selectedSpecialRoles = outcomes[Math.floor(random() * outcomes.length)];
	const wolfCount = playerCount === 10 ? 3 : 4;
	const selectedEvilRoles = [...selectedSpecialRoles, ...Array.from({ length: wolfCount - selectedSpecialRoles.length }, () => PlayerRole.werewolf)];
	const selectedGoodRoles = [...ULTIMATE_TRIAL_FIXED_GOOD_ROLES, ULTIMATE_TRIAL_GOOD_RANDOM_POOL[Math.floor(random() * ULTIMATE_TRIAL_GOOD_RANDOM_POOL.length)]];
	const fixedVillagers = Array.from({ length: wolfCount }, () => PlayerRole.villager);
	const allRoles = [
		...selectedEvilRoles,
		...selectedGoodRoles,
		...fixedVillagers
	];
	const roleCounts = {};
	let villagerCount = 0;
	for (const role of allRoles) if (role === PlayerRole.villager) villagerCount++;
	else roleCounts[role] = (roleCounts[role] || 0) + 1;
	return writeSetting({
		kind: "append-roster-mode",
		setting: checkboxSetting(gameType, roleCounts, villagerCount),
		suffix: "R"
	});
}
function generateSettingFor10UltimateTrial(gameType, includeWolfSiblings = true) {
	return generateSettingForUltimateTrial(gameType, 10, includeWolfSiblings);
}
function getCompanyDisplayName(company) {
	return COMPANY_DISPLAY_NAMES[company] || company;
}
function getModelDisplayName(fullModelName) {
	const parts = fullModelName.split("/");
	return parts.length > 1 ? parts[1] : fullModelName;
}
function getBenchmarkModels(gameSetting) {
	const models = BENCHMARK_DISPLAYED_MODELES[gameSetting];
	if (!models) throw new Error(`Unknown game setting for benchmark models: ${gameSetting}`);
	return models;
}
function getBenchmarkRankingModels(gameSetting) {
	const models = BENCHMARK_RANKING_MODELES[gameSetting];
	if (!models) throw new Error(`Unknown game setting for benchmark ranking: ${gameSetting}`);
	return models;
}
function getCompanyModels(company) {
	const models = INTERNALCOMPARISON_DISPLAYED_MODELES[company];
	if (!models) throw new Error(`Unknown company for internal comparison: ${company}`);
	return models;
}
function getAllCompanies() {
	return Object.keys(INTERNALCOMPARISON_DISPLAYED_MODELES);
}

//#endregion
export { AI_PARAMS, ANONYMOUS_FREE_GAME_SETTING, API_REQUEST_BODY_LIMIT_BYTES, BENCHMARK_DISPLAYED_MODELES, BENCHMARK_RANKING_MODELES, BRAWL_ELIGIBLE_ROLES, COMPANY_DISPLAY_NAMES, CUSTOM_GAME_ELIGIBLE_ROLES, CUSTOM_ROSTER_EXTRA_ROLES, CUSTOM_ROSTER_FIXED_ROLES, CUSTOM_ROSTER_WOLF_CATALOG, CUSTOM_ROSTER_WOLF_CHOICES, CUSTOM_ROSTER_WOLF_PAIR, DataSettingOptions, EVIL_ROLE_POOL, FACTION_EMOJIS, FIXED_GAME_ROSTERS, FIXED_ONLY_ROLES, FREE_GAMES_PER_WINDOW, FREE_GAME_MAX_PLAYERS, FREE_GAME_QUOTA_ANCHOR_MS, FREE_GAME_QUOTA_WINDOW_MS, FREE_GAME_SETTINGS_BY_PLAY_MODE, GOOD_ROLE_POOL, GameSettingOptions, HUMAN_VS_AI_DISABLED_ROLES, IMPLEMENTED_GAME_ROLES, INTERNALCOMPARISON_DISPLAYED_MODELES, IS_PRODUCTION, JEV_COMPARISON_MODEL, LANGUAGE_ENUM_VALUES, MENTISS_REDIS_DB, MENTISS_REDIS_HOST, MENTISS_REDIS_PORT, NightPhaseKey, PLAYER_ROLE_ENUM_VALUES, PORT, PlayerNames, PlayerRole, ROLE_EMOJIS, ROOM_NAME_MAX_LENGTH, RoleCode, RoleFaction, STEAM_CLIENT_USER_AGENT_PRODUCT, STEAM_WEB_API_TICKET_IDENTITY, SUBPHASE_EMOJIS, TEXT_LOG_PLAYER_VOICES, TEXT_LOG_SPEECH_KEYS, TEXT_LOG_TTS_MAX_CHARS, ULTIMATE_TRIAL_EVIL_SPECIAL_POOL, ULTIMATE_TRIAL_FIXED_GOOD_ROLES, ULTIMATE_TRIAL_GOOD_RANDOM_POOL, addCalendarDays, buildCustomRosterSetting, canHoldStream, cleanTtsText, countAndSortRoles, customRosterRoles, escapeSpeechRanges, escapeSpeechText, extractStructuredLogDelta, extractTextLogSpeechRanges, extractTextLogSpeechReferences, findSpeechEmphasis, formatGameRoster, formatGameRosterGroups, formatGameSettingBoardLabel, formatGameSettingDisplay, formatGameSettingGroups, formatRandomBoardDescription, roleCodeSetting as generateGameSettingFromRoleCodeString, checkboxSetting as generateRoleCodeStringFromCheckbox, generateSettingFor10UltimateTrial, generateSettingForUltimateTrial, getAccountLocalDate, getAllCompanies, getAllModelsFromConfig, getBenchmarkModels, getBenchmarkRankingModels, getBrawlEligibleRoles, getBrawlPlayerCount, getCompanyDisplayName, getCompanyModels, getFixedRosterCode, getFreeGameQuotaWindowStart, getGameRosterDisplayStyle, getModelDisplayName, getNextFreeGameQuotaReset, getNextWeeklyQuotaReset, getPlayerCountFromGameSetting, getWeeklyQuotaResetStart, isBrawlGameSetting, isBrawlRoster, isComparedSeat, isCustomRoster, isFixedGameSetting, isFreeGameEligibleSetting, humanVsAiRoleAllowed as isHumanVsAiRoleAllowed, isJevComparisonModel, isNewGameRosterAllowed, isRegisteredAccount, isValidModelInConfig, mergeStructuredLogDelta, normalizeAccountTimeZone, normalizeSpeechRanges, parseGameRoster, parseGameSettingToPlayerConfig, parseRosterSetting, readModelComparisonConfig, readRoster, readSettingMode, rebaseSpeechReferences, renderJournalTextSegments, roleEntry, shuffleArray, structuredLogRevision, walkStructuredJournal, writeSetting };
//# sourceMappingURL=index.mjs.map